import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { getCentralToday } from "@/utils/dateHelpers";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
  DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Users,
  User,
  Calendar,
  CalendarCheck,
  DoorOpen,
  Clock,
  Download,
  LogOut,
  LogIn,
  LayoutDashboard,
  FileSpreadsheet,
  Eye,
  CheckCircle,
  XCircle,
  Receipt,
  ClipboardList,
  Pencil,
  Trash2,
  Image,
  FileText,
  Plus,
  Bed,
  Filter,
  X,
  FileDown,
  Settings,
  Key,
  Mail,
  Globe,
  Save,
  Volume2,
  VolumeX,
  UserCheck,
  Flag,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Search,
  Copy,
  CheckCheck,
  Link,
  Shield,
  FileBarChart,
  Cloud,
  Upload,
  UserX,
  TrendingUp,
  Bell,
  MessageCircle,
  MessageSquare,
  Phone,
  BarChart3,
  Ban,
  Wallet,
  Bitcoin,
  Home,
  DollarSign,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Edit,
  Star,
  QrCode,
  Printer,
  Database,
  HardDrive,
  Bug,
  SprayCan
} from "lucide-react";
import { clearAdminToken, getAdminToken } from "@/lib/adminAuth";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import BitsyQRCode from "@/components/BitsyQRCode";
import ForensicIncidentsView from "@/components/ForensicIncidentsView";
import CryptoSettingsView from "@/components/CryptoSettingsView";
import ActiveNoticesView from "@/components/ActiveNoticesView";

// Tab Components
import { DashboardTab } from "./admin/DashboardTab";
import { SignInTab } from "./admin/SignInTab";
import { BillingTab } from "./admin/BillingTab";
import { RoomsTab } from "./admin/RoomsTab";
import { EmployeesTab } from "./admin/EmployeesTab";
import { MessagesTab } from "./admin/MessagesTab";
import { RoomPreferencesTab } from "./admin/RoomPreferencesTab";
import { GuestsTab } from "./admin/GuestsTab";
import { GuaranteeTab } from "./admin/GuaranteeTab";
import { CryptoTab } from "./admin/CryptoTab";
import { ProfitabilityTab } from "./admin/ProfitabilityTab";
import { SettingsTab } from "./admin/SettingsTab";
import { PestControlTab } from "./admin/PestControlTab";
import { BeddingTab } from "./admin/BeddingTab";
import { AdminProvider, useAdmin } from "./admin/AdminContext";
import { DbHealthBanner } from "./admin/DbHealthBanner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const withAdminAuthToken = (url) => {
  const token = getAdminToken();
  if (!token) return url;

  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}auth_token=${encodeURIComponent(token)}`;
};

export default function AdminDashboard() {
  return (
    <AdminProvider>
      <AdminDashboardInner />
    </AdminProvider>
  );
}

function AdminDashboardInner() {
  const { stats, records, rooms, employees, fetchData, fetchRooms, fetchEmployees, startDate, endDate, setStartDate, setEndDate, isFiltered, handleApplyFilter, handleClearFilter, syncStatus, setSyncStatus, markingNoBill, setMarkingNoBill, handleMarkNoBill, loading: contextLoading } = useAdmin();
  // Debug logging
  console.log('[AdminDashboard] Component rendering...');
  
  const [selectedSignature, setSelectedSignature] = useState(null);
  const [activeView, setActiveView] = useState("dashboard");
  const [editingRecord, setEditingRecord] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  
  // Filter state (local to dashboard UI)
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [roomSearch, setRoomSearch] = useState("");
  const [nameSearch, setNameSearch] = useState("");
  
  // Room management state
  const [showRoomDialog, setShowRoomDialog] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [roomSortOrder, setRoomSortOrder] = useState('asc'); // 'asc' or 'desc' for room number sorting
  const [roomForm, setRoomForm] = useState({
    room_number: "",
    room_type: "Standard",
    floor: "1",
    notes: ""
  });
  const [deleteRoomConfirm, setDeleteRoomConfirm] = useState(null);
  
  // Employee management state (local UI)
  const [employeeSortBy, setEmployeeSortBy] = useState('name'); // 'name', 'id', 'date'
  const [employeeSortOrder, setEmployeeSortOrder] = useState('asc'); // 'asc', 'desc'
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [showEmployeeDialog, setShowEmployeeDialog] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeForm, setEmployeeForm] = useState({
    employee_number: "",
    name: ""
  });
  const [deleteEmployeeConfirm, setDeleteEmployeeConfirm] = useState(null);
  const [bulkImportText, setBulkImportText] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [collectingEmployees, setCollectingEmployees] = useState(false);
  
  // Employee Messages state
  const [employeeMessages, setEmployeeMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [messageForm, setMessageForm] = useState({
    employee_number: "",
    employee_name: "",
    message: ""
  });
  
  // Employee Room Preferences state
  const [roomPreferences, setRoomPreferences] = useState([]);
  const [loadingPreferences, setLoadingPreferences] = useState(false);
  const [showPreferenceDialog, setShowPreferenceDialog] = useState(false);
  const [editingPreference, setEditingPreference] = useState(null);
  const [preferenceForm, setPreferenceForm] = useState({
    employee_number: "",
    employee_name: "",
    preferred_room: "",
    preferred_floor: "",
    preferred_bed_type: "",
    notes: ""
  });
  
  // Guest management state (verification)
  const [registeredGuests, setRegisteredGuests] = useState([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  
  // Blocked rooms state (other guests)
  const [blockedRooms, setBlockedRooms] = useState([]);
  const [showBlockRoomDialog, setShowBlockRoomDialog] = useState(false);
  const [blockRoomForm, setBlockRoomForm] = useState({ 
    room_number: "", 
    room_type: "",
    guest_name: "", 
    email: "",
    phone: "",
    address: "",
    check_in_date: getCentralToday(),
    check_out_date: "",
    room_rate: "",
    notes: "",
    is_reservation: false
  });
  const [blockedRoomStats, setBlockedRoomStats] = useState(null);
  const [reservations, setReservations] = useState([]);
  const [bitsyBookings, setBitsyBookings] = useState([]);
  const [showBitsyBookings, setShowBitsyBookings] = useState(false);
  const [editingBitsyBooking, setEditingBitsyBooking] = useState(null);
  const [bitsyEditForm, setBitsyEditForm] = useState({
    guest_name: "",
    phone: "",
    email: "",
    room_type: "",
    check_in_date: "",
    check_out_date: ""
  });
  const [checkingInBitsyBooking, setCheckingInBitsyBooking] = useState(null);
  const [bitsyCheckInRoom, setBitsyCheckInRoom] = useState("");
  const [nonRailroadHistory, setNonRailroadHistory] = useState([]);
  const [showNonRailroadHistory, setShowNonRailroadHistory] = useState(false);
  const [blockedGuests, setBlockedGuests] = useState([]);
  const [showBlockedGuests, setShowBlockedGuests] = useState(false);
  const [blockGuestDialog, setBlockGuestDialog] = useState(false);
  const [blockGuestForm, setBlockGuestForm] = useState({
    guest_name: '',
    email: '',
    phone: '',
    reason: ''
  });
  
  // Edit non-railroad guest state
  const [showEditGuestDialog, setShowEditGuestDialog] = useState(false);
  const [editGuestForm, setEditGuestForm] = useState({
    id: '',
    room_number: '',
    guest_name: '',
    email: '',
    phone: '',
    check_in_date: '',
    check_out_date: '',
    room_rate: '',
    notes: ''
  });
  
  // Multi-room booking state
  const [showMultiRoomBooking, setShowMultiRoomBooking] = useState(false);
  const [multiRoomForm, setMultiRoomForm] = useState({
    guest_name: '',
    email: '',
    phone: '',
    address: '',
    check_in_date: '',
    check_out_date: '',
    notes: '',
    send_confirmation_email: true
  });
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [multiRoomLoading, setMultiRoomLoading] = useState(false);
  const [lastConfirmation, setLastConfirmation] = useState(null);
  
  // Email Alert Settings state
  const [emailAlertSettings, setEmailAlertSettings] = useState({
    recipients: [],
    sold_out_enabled: true,
    rooms_available_enabled: true,
    heads_up_enabled: true,
    heads_up_threshold: 4,
    custom_subject_prefix: "Hodler Inn"
  });
  const [newRecipientEmail, setNewRecipientEmail] = useState("");
  const [emailPreview, setEmailPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Telegram Settings state
  const [telegramSettings, setTelegramSettings] = useState({
    checkin_channel: { chat_id: "", enabled: true, quiet_hours_enabled: false, quiet_hours_start: "22:00", quiet_hours_end: "07:00" },
    checkout_channel: { chat_id: "", enabled: true, quiet_hours_enabled: false, quiet_hours_start: "22:00", quiet_hours_end: "07:00" },
    reports_channel: { chat_id: "", enabled: true, quiet_hours_enabled: false, quiet_hours_start: "22:00", quiet_hours_end: "07:00" },
    cleaning_channel: { chat_id: "", enabled: true, quiet_hours_enabled: false, quiet_hours_start: "22:00", quiet_hours_end: "07:00" },
    requests_channel: { chat_id: "", enabled: true, quiet_hours_enabled: false, quiet_hours_start: "22:00", quiet_hours_end: "07:00" },
    default_channel: { chat_id: "", enabled: true, quiet_hours_enabled: false, quiet_hours_start: "22:00", quiet_hours_end: "07:00" }
  });
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramTestingChannel, setTelegramTestingChannel] = useState(null);
  const [discoveredGroups, setDiscoveredGroups] = useState([]);
  const [discoverGroupsLoading, setDiscoverGroupsLoading] = useState(false);
  const [showGroupSelector, setShowGroupSelector] = useState(false);
  const [selectedChannelForGroup, setSelectedChannelForGroup] = useState(null);
  
  // Guarantee report state
  const [guaranteeReport, setGuaranteeReport] = useState(null);
  const [guaranteeLoading, setGuaranteeLoading] = useState(false);
  const [guaranteeDateRange, setGuaranteeDateRange] = useState({ start: "", end: "" });
  
  // Daily Billing Summary state
  const [billingSummary, setBillingSummary] = useState(null);
  const [billingSummaryLoading, setBillingSummaryLoading] = useState(false);
  const [showBillingSummary, setShowBillingSummary] = useState(false);
  
  // Internal Revenue Analytics state (for internal use only)
  const [revenueAnalytics, setRevenueAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  // Manual Entry state
  const [showManualEntryForm, setShowManualEntryForm] = useState(false);
  const [manualEntryForm, setManualEntryForm] = useState({
    employee_id: "",
    first_name: "",
    last_name: "",
    room_number: "",
    check_in_date: getCentralToday(),
    check_in_time: "14:00",
    check_out_date: "",
    check_out_time: "11:00",
    is_checked_out: false
  });
  const [manualEntryLoading, setManualEntryLoading] = useState(false);
  
  // Expected Arrivals state (CPKC)
  const [expectedArrivals, setExpectedArrivals] = useState([]);
  const [expectedCheckouts, setExpectedCheckouts] = useState([]);
  const [checkingCpkcEmails, setCheckingCpkcEmails] = useState(false);
  
  // Turned Away Guests state
  const [turnedAwayGuests, setTurnedAwayGuests] = useState([]);
  const [showTurnedAwayForm, setShowTurnedAwayForm] = useState(false);
  const [turnedAwayStats, setTurnedAwayStats] = useState({ total_lost_revenue: 0, single_bed_count: 0, double_bed_count: 0 });
  const [turnedAwayForm, setTurnedAwayForm] = useState({
    date: getCentralToday(),
    guest_name: "Walk-in Guest",
    bed_type: "single",
    room_price: 85.00,
    reason: "Holding rooms for CPKC",
    notes: ""
  });
  
  // Crypto Payments state
  const [cryptoPayments, setCryptoPayments] = useState([]);
  const [cryptoStats, setCryptoStats] = useState({
    total_payments: 0,
    total_usd: 0,
    by_network: {},
    by_token: {}
  });
  const [cryptoFilter, setCryptoFilter] = useState({ network: 'all', token: 'all' });
  
  // Reservation Check-in Dialog state
  const [checkinDialog, setCheckinDialog] = useState({
    open: false,
    reservationId: null,
    guestName: '',
    roomNumber: ''
  });
  
  // Profitability Report state
  const [profitabilityData, setProfitabilityData] = useState(null);
  const [profitabilityLoading, setProfitabilityLoading] = useState(false);
  const [profitabilityMonth, setProfitabilityMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [monthlyExpenses, setMonthlyExpenses] = useState({
    utilities: '',
    labor: '',
    supplies_per_room: '2.50',
    property_tax: '',
    insurance: '',
    mortgage: '',
    other: ''
  });
  const [savingExpenses, setSavingExpenses] = useState(false);
  
  // Portal Settings state
  const [portalSettings, setPortalSettings] = useState({
    api_global_username: "",
    api_global_password: "",
    alert_email: "",
    api_global_password_set: false,
    voice_enabled: true,
    voice_volume: 1.0,
    voice_speed: 0.85,
    telegram_chat_id: "",
    public_api_key: "",
    nightly_rate: 75.0,
    railroad_room_rate: 56.0,
    api_commission_rate: 10.0,
    single_room_rate: 79.0,
    double_room_rate: 89.0,
    sales_tax_rate: 10.3,
    chatbot_max_rooms: 3,
    guaranteed_rooms: 25,
    bitsy_enabled: true,
    bitsy_crew_enabled: true,
    email_reports_enabled: false,
    email_smtp_host: "smtp.zoho.com",
    email_smtp_port: 587,
    email_sender: "",
    email_password_set: false,
    email_recipient: "",
    email_report_time: "00:00"
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [testingZoho, setTestingZoho] = useState(false);
  const [uploadingZoho, setUploadingZoho] = useState(false);
  const [zohoUploadDate, setZohoUploadDate] = useState(getCentralToday());
  const [backingUpDatabase, setBackingUpDatabase] = useState(false);
  const [lastBackupResult, setLastBackupResult] = useState(null);
  const [runningSyncTest, setRunningSyncTest] = useState(false);
  const [syncTargetDate, setSyncTargetDate] = useState("");
  const [syncIncludePrevDay, setSyncIncludePrevDay] = useState(false);
  const [employeeScanStartDate, setEmployeeScanStartDate] = useState("");
  const [employeeScanEndDate, setEmployeeScanEndDate] = useState("");
  
  const navigate = useNavigate();
  const location = useLocation();

  // Persist activeView to URL hash for refresh support
  useEffect(() => {
    // Use search params instead of hash to avoid conflict with HashRouter
    // In HashRouter, the search params come from the hash portion: /#/admin/dashboard?tab=billing
    const hashParts = window.location.hash.split('?');
    const searchStr = hashParts.length > 1 ? hashParts[1] : '';
    const searchParams = new URLSearchParams(searchStr);
    const tab = searchParams.get('tab');
    if (tab && ['dashboard', 'signin', 'billing', 'rooms', 'employees', 'messages', 'guests', 'guarantee', 'crypto', 'profitability', 'pest-control', 'bedding', 'notices', 'settings'].includes(tab)) {
      setActiveView(tab);
    }
  }, [location]);

  // Update URL when activeView changes
  const handleViewChange = (view) => {
    setActiveView(view);
    // Use search params for tab navigation to avoid hash conflicts
    // For HashRouter: /#/admin/dashboard?tab=billing
    const basePath = window.location.hash.split('?')[0];
    window.history.replaceState(null, '', `${basePath}?tab=${view}`);
  };

  useEffect(() => {
    console.log('[AdminDashboard] useEffect running, checking token...');
    const token = getAdminToken();
    console.log('[AdminDashboard] Token exists:', !!token);
    if (!token) {
      console.log('[AdminDashboard] No token, redirecting to login...');
      navigate("/admin");
      return;
    }
    console.log('[AdminDashboard] Token valid, fetching data...');
    fetchData();
    fetchRooms();
    fetchSettings();
    fetchEmployees();
    fetchEmployeeMessages();
    fetchRoomPreferences();
    fetchRegisteredGuests();
    fetchBlockedRooms();
    fetchBlockedRoomStats();
    fetchReservations();
    fetchBitsyBookings();
    fetchEmailAlertSettings();
    fetchTelegramSettings();
    fetchExpectedArrivals();
    fetchExpectedCheckouts();
    fetchCryptoPayments();
  }, [navigate]);

  const fetchRegisteredGuests = async () => {
    setLoadingGuests(true);
    try {
      const response = await axios.get(`${API}/admin/guests`);
      setRegisteredGuests(response.data);
    } catch (error) {
      console.error("Failed to load registered guests");
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleVerifyGuest = async (employeeNumber) => {
    try {
      await axios.post(`${API}/admin/guests/${employeeNumber}/verify`);
      toast.success("Guest verified!");
      fetchRegisteredGuests();
    } catch (error) {
      toast.error("Failed to verify guest");
    }
  };

  const handleFlagGuest = async (employeeNumber) => {
    try {
      await axios.post(`${API}/admin/guests/${employeeNumber}/flag`);
      toast.warning("Guest flagged for review");
      fetchRegisteredGuests();
    } catch (error) {
      toast.error("Failed to flag guest");
    }
  };

  const handleBulkVerifyAll = async () => {
    const pendingGuests = registeredGuests.filter(g => !g.is_verified);
    if (pendingGuests.length === 0) {
      toast.info("No pending guests to verify");
      return;
    }
    
    try {
      const employeeNumbers = pendingGuests.map(g => g.employee_number);
      await axios.post(`${API}/admin/guests/bulk-verify`, { employee_numbers: employeeNumbers });
      toast.success(`Verified ${pendingGuests.length} guests!`);
      fetchRegisteredGuests();
    } catch (error) {
      toast.error("Failed to bulk verify guests");
    }
  };

  // Blocked rooms functions (Other Guests)
  const fetchBlockedRooms = async () => {
    try {
      const response = await axios.get(`${API}/admin/rooms/blocked`);
      setBlockedRooms(response.data);
    } catch (error) {
      console.error("Failed to load blocked rooms");
    }
  };

  const fetchBlockedRoomStats = async () => {
    try {
      const response = await axios.get(`${API}/admin/rooms/blocked/stats`);
      setBlockedRoomStats(response.data);
    } catch (error) {
      console.error("Failed to load blocked room stats");
    }
  };

  const fetchReservations = async () => {
    try {
      const response = await axios.get(`${API}/admin/rooms/reservations`);
      setReservations(response.data);
    } catch (error) {
      console.error("Failed to load reservations");
    }
  };

  const fetchBitsyBookings = async () => {
    try {
      const response = await axios.get(`${API}/admin/bitsy-bookings`);
      setBitsyBookings(response.data);
    } catch (error) {
      console.error("Failed to load Bitsy bookings");
    }
  };

  const handleEditBitsyBooking = (booking) => {
    setEditingBitsyBooking(booking);
    setBitsyEditForm({
      guest_name: booking.guest_name || "",
      phone: booking.phone || "",
      email: booking.email || "",
      room_type: booking.room_type || "",
      check_in_date: booking.check_in_date || "",
      check_out_date: booking.check_out_date || ""
    });
  };

  const handleSaveBitsyBooking = async () => {
    if (!editingBitsyBooking) return;
    try {
      await axios.put(`${API}/admin/bitsy-bookings/${editingBitsyBooking.confirmation_number}`, bitsyEditForm);
      toast.success("Booking updated successfully");
      setEditingBitsyBooking(null);
      fetchBitsyBookings();
    } catch (error) {
      toast.error("Failed to update booking");
    }
  };

  const handleCancelBitsyBooking = async (confirmationNumber) => {
    if (!confirm(`Are you sure you want to cancel booking ${confirmationNumber}?`)) return;
    try {
      await axios.delete(`${API}/admin/bitsy-bookings/${confirmationNumber}`);
      toast.success("Booking cancelled");
      fetchBitsyBookings();
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const fetchNonRailroadHistory = async (startDateOverride, endDateOverride) => {
    try {
      // Use the per-card date filters if the caller passed them; otherwise fetch the
      // full history. We intentionally do NOT inherit the admin-wide startDate/
      // endDate (set in the Billing/Records tabs) because this card has its own
      // dedicated date pickers and silently inheriting another tab's filter
      // caused the "No non-railroad guest history found" empty-state bug
      // even when 56 records existed.
      let url = `${API}/admin/rooms/non-railroad-history`;
      const params = [];
      if (startDateOverride) params.push(`start_date=${startDateOverride}`);
      if (endDateOverride) params.push(`end_date=${endDateOverride}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      const response = await axios.get(url);
      setNonRailroadHistory(response.data);
    } catch (error) {
      console.error("Failed to load non-railroad guest history");
    }
  };

  const fetchBlockedGuests = async () => {
    try {
      const response = await axios.get(`${API}/admin/blocked-guests`);
      setBlockedGuests(response.data);
    } catch (error) {
      console.error("Failed to load blocked guests");
    }
  };

  // Check in Bitsy booking (assign room and mark as checked in)
  const handleCheckInBitsyBooking = async () => {
    if (!checkingInBitsyBooking || !bitsyCheckInRoom) {
      toast.error("Please select a room");
      return;
    }
    try {
      await axios.post(`${API}/admin/bitsy-bookings/${checkingInBitsyBooking.confirmation_number}/checkin`, {
        room_number: bitsyCheckInRoom
      });
      toast.success(`${checkingInBitsyBooking.guest_name} checked in to Room ${bitsyCheckInRoom}!`);
      setCheckingInBitsyBooking(null);
      setBitsyCheckInRoom("");
      fetchBitsyBookings();
      fetchBlockedRooms();
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check in guest");
    }
  };

  // Edit non-railroad guest functions
  const handleEditGuest = (guest) => {
    setEditGuestForm({
      id: guest.id,
      room_number: guest.room_number || guest.room_type || '',
      guest_name: guest.guest_name || '',
      email: guest.email || '',
      phone: guest.phone || '',
      check_in_date: guest.check_in_date || '',
      check_out_date: guest.check_out_date || '',
      room_rate: guest.room_rate || guest.total_amount || '',
      notes: guest.notes || ''
    });
    setShowEditGuestDialog(true);
  };

  const handleSaveGuestEdit = async () => {
    if (!editGuestForm.id) {
      toast.error("No guest selected");
      return;
    }
    try {
      const response = await axios.put(`${API}/admin/rooms/blocked/${editGuestForm.id}`, {
        room_number: editGuestForm.room_number,
        guest_name: editGuestForm.guest_name,
        email: editGuestForm.email,
        phone: editGuestForm.phone,
        check_in_date: editGuestForm.check_in_date,
        check_out_date: editGuestForm.check_out_date,
        room_rate: parseFloat(editGuestForm.room_rate) || 0,
        notes: editGuestForm.notes
      });
      toast.success("Guest information updated");
      setShowEditGuestDialog(false);
      fetchBlockedRooms();
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update guest");
    }
  };

  const handleBlockGuest = async () => {
    if (!blockGuestForm.guest_name || !blockGuestForm.reason) {
      toast.error("Guest name and reason are required");
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/blocked-guests`, blockGuestForm);
      if (response.data.existing) {
        toast.info("Guest is already on the blocked list");
      } else {
        toast.success("Guest blocked successfully");
      }
      setBlockGuestDialog(false);
      setBlockGuestForm({ guest_name: '', email: '', phone: '', reason: '' });
      fetchBlockedGuests();
    } catch (error) {
      toast.error("Failed to block guest");
    }
  };

  const handleUnblockGuest = async (guestId) => {
    if (!window.confirm("Are you sure you want to unblock this guest?")) return;
    
    try {
      await axios.delete(`${API}/admin/blocked-guests/${guestId}`);
      toast.success("Guest unblocked");
      fetchBlockedGuests();
    } catch (error) {
      toast.error("Failed to unblock guest");
    }
  };

  const handleQuickBlockGuest = (guest) => {
    setBlockGuestForm({
      guest_name: guest.guest_name || '',
      email: guest.email || '',
      phone: guest.phone || '',
      reason: ''
    });
    setBlockGuestDialog(true);
  };

  // Multi-room booking functions
  const addRoomToBooking = (roomType) => {
    const newRoom = {
      id: Date.now(),
      room_type: roomType,
      room_number: '',
      room_rate: roomType === 'double' ? portalSettings.double_room_rate : portalSettings.single_room_rate
    };
    setSelectedRooms([...selectedRooms, newRoom]);
  };

  const removeRoomFromBooking = (roomId) => {
    setSelectedRooms(selectedRooms.filter(r => r.id !== roomId));
  };

  const updateRoomInBooking = (roomId, field, value) => {
    setSelectedRooms(selectedRooms.map(r => 
      r.id === roomId ? { ...r, [field]: value } : r
    ));
  };

  const handleMultiRoomBooking = async () => {
    if (!multiRoomForm.guest_name || !multiRoomForm.check_in_date || !multiRoomForm.check_out_date) {
      toast.error("Please fill in guest name and dates");
      return;
    }
    if (selectedRooms.length === 0) {
      toast.error("Please add at least one room");
      return;
    }

    setMultiRoomLoading(true);
    try {
      const response = await axios.post(`${API}/admin/rooms/multi-book`, {
        ...multiRoomForm,
        rooms: selectedRooms.map(r => ({
          room_number: r.room_number || null,
          room_type: r.room_type,
          room_rate: r.room_rate
        }))
      });
      
      setLastConfirmation(response.data);
      toast.success(`Booked ${response.data.total_rooms} room(s)! Confirmation: ${response.data.confirmation_number}`);
      
      // Reset form
      setMultiRoomForm({
        guest_name: '',
        email: '',
        phone: '',
        address: '',
        check_in_date: '',
        check_out_date: '',
        notes: '',
        send_confirmation_email: true
      });
      setSelectedRooms([]);
      
      // Refresh reservations
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create booking");
    } finally {
      setMultiRoomLoading(false);
    }
  };

  const copyConfirmationToClipboard = (confirmationNumber) => {
    navigator.clipboard.writeText(confirmationNumber);
    toast.success(`Confirmation ${confirmationNumber} copied to clipboard!`);
  };

  const handleBlockRoom = async () => {
    // For reservations, room_type is enough; for check-in, need room_number
    const isReservation = blockRoomForm.is_reservation || (blockRoomForm.check_in_date > getCentralToday());
    
    if (!isReservation && !blockRoomForm.room_number) {
      toast.error("Please select a room for check-in");
      return;
    }
    if (isReservation && !blockRoomForm.room_number && !blockRoomForm.room_type) {
      toast.error("Please select a room or room type for reservation");
      return;
    }
    if (!blockRoomForm.guest_name) {
      toast.error("Please enter guest name");
      return;
    }
    
    // Prevent double submission
    const submitBtn = document.querySelector('[data-testid="non-railroad-checkin-btn"]');
    if (submitBtn) submitBtn.disabled = true;
    
    try {
      const response = await axios.post(`${API}/admin/rooms/block`, {
        room_number: blockRoomForm.room_number || null,
        room_type: blockRoomForm.room_type,
        guest_name: blockRoomForm.guest_name,
        email: blockRoomForm.email,
        phone: blockRoomForm.phone,
        address: blockRoomForm.address,
        check_in_date: blockRoomForm.check_in_date,
        check_out_date: blockRoomForm.check_out_date,
        room_rate: parseFloat(blockRoomForm.room_rate) || 0,
        notes: blockRoomForm.notes,
        is_reservation: isReservation
      });
      const action = isReservation ? "Reservation created" : "Guest checked in";
      toast.success(`${action}: ${blockRoomForm.guest_name}`);
      setShowBlockRoomDialog(false);
      
      // Reset form IMMEDIATELY after success
      setBlockRoomForm({ 
        room_number: "", 
        room_type: "",
        guest_name: "", 
        email: "",
        phone: "",
        address: "",
        check_in_date: getCentralToday(),
        check_out_date: "",
        room_rate: "",
        notes: "",
        is_reservation: false
      });
      
      // Refresh data
      fetchRooms();
      fetchBlockedRooms();
      fetchBlockedRoomStats();
      fetchReservations();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Failed to process booking";
      toast.error(errorMsg);
    } finally {
      // Re-enable button
      if (submitBtn) submitBtn.disabled = false;
    }
  };

  const handleUnblockRoom = async (roomNumber) => {
    try {
      await axios.post(`${API}/admin/rooms/unblock/${roomNumber}`);
      toast.success(`Guest checked out from Room ${roomNumber}`);
      fetchRooms();
      fetchBlockedRooms();
      fetchBlockedRoomStats();
    } catch (error) {
      toast.error("Failed to check out guest");
    }
  };

  // Open check-in dialog for a reservation
  const openCheckinDialog = (reservation) => {
    setCheckinDialog({
      open: true,
      reservationId: reservation.id,
      guestName: reservation.guest_name,
      roomNumber: reservation.room_number || ''
    });
  };

  const handleCheckinReservation = async (reservationId, roomNumber) => {
    if (!roomNumber) {
      toast.error("Please enter a room number");
      return;
    }
    try {
      await axios.post(`${API}/admin/rooms/reservations/${reservationId}/checkin`, null, {
        params: { room_number: roomNumber }
      });
      toast.success("Reservation checked in successfully");
      setCheckinDialog({ open: false, reservationId: null, guestName: '', roomNumber: '' });
      fetchRooms();
      fetchBlockedRooms();
      fetchReservations();
      fetchBlockedRoomStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check in reservation");
    }
  };

  const handleCancelReservation = async (reservationId) => {
    try {
      await axios.delete(`${API}/admin/rooms/reservations/${reservationId}`);
      toast.success("Reservation cancelled");
      fetchReservations();
    } catch (error) {
      toast.error("Failed to cancel reservation");
    }
  };

  const handleConfirmReservation = async (reservationId) => {
    try {
      await axios.post(`${API}/admin/rooms/reservations/${reservationId}/confirm`);
      toast.success("Reservation confirmed! Guest called to confirm booking.");
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to confirm reservation");
    }
  };

  const handleSendConfirmationEmail = async (bookingId, guestEmail) => {
    if (!guestEmail) {
      toast.error("No email address for this guest");
      return;
    }
    try {
      await axios.post(`${API}/admin/rooms/booking/${bookingId}/send-confirmation`);
      toast.success(`Confirmation email sent to ${guestEmail}`);
      fetchBlockedRooms();
      fetchReservations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send email");
    }
  };

  // Email Alert Settings functions
  const fetchEmailAlertSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/email-alerts/settings`);
      setEmailAlertSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch email alert settings");
    }
  };

  const updateEmailAlertSettings = async (newSettings) => {
    try {
      await axios.post(`${API}/admin/email-alerts/settings`, newSettings);
      toast.success("Email alert settings updated");
      fetchEmailAlertSettings();
    } catch (error) {
      toast.error("Failed to update settings");
    }
  };

  const addEmailRecipient = async () => {
    if (!newRecipientEmail || !newRecipientEmail.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    try {
      await axios.post(`${API}/admin/email-alerts/recipients/add?email=${encodeURIComponent(newRecipientEmail)}`);
      toast.success(`Added ${newRecipientEmail}`);
      setNewRecipientEmail("");
      fetchEmailAlertSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add recipient");
    }
  };

  const removeEmailRecipient = async (email) => {
    try {
      await axios.delete(`${API}/admin/email-alerts/recipients?email=${encodeURIComponent(email)}`);
      toast.success(`Removed ${email}`);
      fetchEmailAlertSettings();
    } catch (error) {
      toast.error("Failed to remove recipient");
    }
  };

  // Telegram Settings Functions
  const fetchTelegramSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/telegram-settings`);
      setTelegramSettings(res.data);
    } catch (error) {
      console.error("Failed to fetch Telegram settings:", error);
    }
  };

  const saveTelegramSettings = async () => {
    setTelegramLoading(true);
    try {
      await axios.post(`${API}/admin/telegram-settings`, telegramSettings);
      toast.success("Telegram settings saved!");
    } catch (error) {
      toast.error("Failed to save Telegram settings");
    } finally {
      setTelegramLoading(false);
    }
  };

  const testTelegramChannel = async (channel) => {
    setTelegramTestingChannel(channel);
    try {
      const chatId = telegramSettings[`${channel}_channel`]?.chat_id;
      const res = await axios.post(`${API}/admin/telegram-test?channel=${channel}&chat_id=${chatId || ''}`);
      if (res.data.success) {
        toast.success(res.data.message);
      } else {
        toast.error(res.data.message);
      }
    } catch (error) {
      toast.error("Failed to send test message");
    } finally {
      setTelegramTestingChannel(null);
    }
  };

  const updateTelegramChannel = (channel, field, value) => {
    setTelegramSettings(prev => ({
      ...prev,
      [`${channel}_channel`]: {
        ...prev[`${channel}_channel`],
        [field]: value
      }
    }));
  };

  const discoverTelegramGroups = async () => {
    setDiscoverGroupsLoading(true);
    try {
      const res = await axios.get(`${API}/admin/telegram-discover-groups`);
      if (res.data.success) {
        setDiscoveredGroups(res.data.groups || []);
        if (res.data.groups?.length > 0) {
          setShowGroupSelector(true);
          toast.success(`Found ${res.data.groups.length} group(s)!`);
        } else {
          toast.info("No groups found. Add the bot to a Telegram group first, then try again.");
        }
      } else {
        toast.error(res.data.message || "Failed to discover groups");
      }
    } catch (error) {
      console.error("Failed to discover Telegram groups:", error);
      toast.error("Failed to discover groups. Check the console for details.");
    } finally {
      setDiscoverGroupsLoading(false);
    }
  };

  const selectGroupForChannel = (group, channel) => {
    updateTelegramChannel(channel, 'chat_id', group.chat_id);
    toast.success(`Selected "${group.title}" for ${channel.replace('_', ' ')} channel`);
    setShowGroupSelector(false);
    setSelectedChannelForGroup(null);
  };

  const toggleRecipientAlert = async (email, alertType, currentAlerts) => {
    const newAlerts = currentAlerts.includes(alertType)
      ? currentAlerts.filter(a => a !== alertType)
      : [...currentAlerts, alertType];
    
    try {
      await axios.put(`${API}/admin/email-alerts/recipients/alerts?email=${encodeURIComponent(email)}`, newAlerts);
      fetchEmailAlertSettings();
    } catch (error) {
      toast.error("Failed to update recipient alerts");
    }
  };

  // Helper to get recipient alerts (handles both old string format and new object format)
  const getRecipientAlerts = (recipient) => {
    if (typeof recipient === 'string') {
      return ['sold_out', 'rooms_available', 'heads_up', 'daily_status'];
    }
    return recipient.alerts || [];
  };

  const getRecipientEmail = (recipient) => {
    if (typeof recipient === 'string') return recipient;
    return recipient.email || '';
  };

  const sendTestAlert = async () => {
    try {
      const res = await axios.post(`${API}/admin/email-alerts/test`);
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    }
  };

  const previewEmailAlert = async (alertType) => {
    setPreviewLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-alerts/preview/${alertType}`);
      setEmailPreview(res.data);
    } catch (error) {
      toast.error("Failed to load email preview");
    }
    setPreviewLoading(false);
  };

  const handleMarkRoomDirty = async (roomNumber) => {
    try {
      await axios.post(`${API}/admin/rooms/${roomNumber}/mark-dirty`);
      toast.success(`Room ${roomNumber} marked as dirty`);
      fetchRooms();
    } catch (error) {
      toast.error("Failed to update room status");
    }
  };

  const handleMarkRoomClean = async (roomNumber) => {
    try {
      await axios.post(`${API}/admin/rooms/${roomNumber}/mark-clean`);
      toast.success(`Room ${roomNumber} marked as clean`);
      fetchRooms();
    } catch (error) {
      toast.error("Failed to update room status");
    }
  };

  const fetchGuaranteeReport = async (startDate = "", endDate = "") => {
    setGuaranteeLoading(true);
    try {
      let url = `${API}/admin/guarantee-report`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setGuaranteeReport(response.data);
    } catch (error) {
      toast.error("Failed to load guarantee report");
    } finally {
      setGuaranteeLoading(false);
    }
  };

  const downloadGuaranteeReport = async () => {
    try {
      let url = `${API}/admin/export-guarantee-report`;
      const params = [];
      if (guaranteeDateRange.start) params.push(`start_date=${guaranteeDateRange.start}`);
      if (guaranteeDateRange.end) params.push(`end_date=${guaranteeDateRange.end}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'hodler_inn_guarantee_report.xlsx';
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Guarantee report downloaded!");
    } catch (error) {
      toast.error("Failed to download report");
    }
  };

  // Daily Billing Summary functions
  const fetchBillingSummary = async () => {
    setBillingSummaryLoading(true);
    try {
      let url = `${API}/admin/billing-summary`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setBillingSummary(response.data);
      setShowBillingSummary(true);
    } catch (error) {
      toast.error("Failed to load billing summary");
    } finally {
      setBillingSummaryLoading(false);
    }
  };

  const downloadBillingSummary = async () => {
    try {
      let url = `${API}/admin/export-billing-summary`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'hodler_inn_daily_billing_summary.xlsx';
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success("Daily billing summary downloaded!");
    } catch (error) {
      toast.error("Failed to download summary");
    }
  };

  // Internal Revenue Analytics (for internal use only)
  const fetchRevenueAnalytics = async () => {
    setAnalyticsLoading(true);
    try {
      let url = `${API}/admin/internal/revenue-analytics`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setRevenueAnalytics(response.data);
      setShowAnalytics(true);
    } catch (error) {
      toast.error("Failed to load analytics");
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Turned Away Guests functions
  const fetchTurnedAwayGuests = async () => {
    try {
      let url = `${API}/admin/turned-away`;
      const params = [];
      if (guaranteeDateRange.start) params.push(`start_date=${guaranteeDateRange.start}`);
      if (guaranteeDateRange.end) params.push(`end_date=${guaranteeDateRange.end}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      
      const response = await axios.get(url);
      setTurnedAwayGuests(response.data.records || []);
      setTurnedAwayStats({
        total_lost_revenue: response.data.total_lost_revenue || 0,
        single_bed_count: response.data.single_bed_count || 0,
        double_bed_count: response.data.double_bed_count || 0
      });
    } catch (error) {
      console.error("Failed to fetch turned away guests:", error);
    }
  };

  const handleLogTurnedAway = async () => {
    try {
      await axios.post(`${API}/admin/turned-away`, turnedAwayForm);
      toast.success("Turned away guest logged successfully");
      setShowTurnedAwayForm(false);
      setTurnedAwayForm({
        date: getCentralToday(),
        guest_name: "Walk-in Guest",
        bed_type: "single",
        room_price: 85.00,
        reason: "Holding rooms for CPKC",
        notes: ""
      });
      fetchTurnedAwayGuests();
      fetchGuaranteeReport(guaranteeDateRange.start, guaranteeDateRange.end);
    } catch (error) {
      toast.error("Failed to log turned away guest");
    }
  };

  const handleDeleteTurnedAway = async (recordId) => {
    if (!confirm("Are you sure you want to delete this record?")) return;
    try {
      await axios.delete(`${API}/admin/turned-away/${recordId}`);
      toast.success("Record deleted");
      fetchTurnedAwayGuests();
      fetchGuaranteeReport(guaranteeDateRange.start, guaranteeDateRange.end);
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  // Crypto Payments functions
  const fetchCryptoPayments = async () => {
    try {
      const response = await axios.get(`${API}/crypto/payments?limit=100`);
      const payments = response.data || [];
      setCryptoPayments(payments);
      
      // Calculate stats
      const stats = {
        total_payments: payments.length,
        total_usd: payments.reduce((sum, p) => sum + (p.amount || 0), 0),
        by_network: {},
        by_token: {}
      };
      
      payments.forEach(p => {
        // By network
        if (!stats.by_network[p.network]) {
          stats.by_network[p.network] = { count: 0, total: 0 };
        }
        stats.by_network[p.network].count++;
        stats.by_network[p.network].total += p.amount || 0;
        
        // By token
        if (!stats.by_token[p.token]) {
          stats.by_token[p.token] = { count: 0, total: 0 };
        }
        stats.by_token[p.token].count++;
        stats.by_token[p.token].total += p.amount || 0;
      });
      
      setCryptoStats(stats);
    } catch (error) {
      console.error("Failed to fetch crypto payments:", error);
    }
  };

  // Employee Messages Functions
  const fetchEmployeeMessages = async () => {
    setLoadingMessages(true);
    try {
      const response = await axios.get(`${API}/admin/employee-messages`);
      setEmployeeMessages(response.data);
    } catch (error) {
      console.error("Failed to load employee messages:", error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleCreateMessage = async () => {
    if (!messageForm.employee_number || !messageForm.message) {
      toast.error("Please enter employee number and message");
      return;
    }
    try {
      await axios.post(`${API}/admin/employee-messages`, messageForm);
      toast.success("Reminder message created successfully");
      setShowMessageDialog(false);
      setMessageForm({ employee_number: "", employee_name: "", message: "" });
      fetchEmployeeMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create message");
    }
  };

  const handleUpdateMessage = async (messageId, updates) => {
    try {
      await axios.put(`${API}/admin/employee-messages/${messageId}`, updates);
      toast.success("Message updated");
      fetchEmployeeMessages();
    } catch (error) {
      toast.error("Failed to update message");
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!confirm("Are you sure you want to delete this message?")) return;
    try {
      await axios.delete(`${API}/admin/employee-messages/${messageId}`);
      toast.success("Message deleted");
      fetchEmployeeMessages();
    } catch (error) {
      toast.error("Failed to delete message");
    }
  };

  const handleToggleMessage = async (message) => {
    await handleUpdateMessage(message.id, { is_active: !message.is_active });
  };

  // Room Preferences Functions
  const fetchRoomPreferences = async () => {
    setLoadingPreferences(true);
    try {
      const response = await axios.get(`${API}/admin/employee-preferences`);
      setRoomPreferences(response.data);
    } catch (error) {
      console.error("Failed to load room preferences:", error);
    } finally {
      setLoadingPreferences(false);
    }
  };

  const handleCreatePreference = async () => {
    if (!preferenceForm.employee_number) {
      toast.error("Please enter employee number");
      return;
    }
    try {
      await axios.post(`${API}/admin/employee-preferences`, preferenceForm);
      toast.success("Room preference saved");
      setShowPreferenceDialog(false);
      setPreferenceForm({ employee_number: "", employee_name: "", preferred_room: "", preferred_floor: "", preferred_bed_type: "", notes: "" });
      fetchRoomPreferences();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save preference");
    }
  };

  const handleUpdatePreference = async () => {
    try {
      await axios.put(`${API}/admin/employee-preferences/${editingPreference.employee_number}`, preferenceForm);
      toast.success("Preference updated");
      setShowPreferenceDialog(false);
      setEditingPreference(null);
      fetchRoomPreferences();
    } catch (error) {
      toast.error("Failed to update preference");
    }
  };

  const handleDeletePreference = async (employeeNumber) => {
    if (!confirm("Are you sure you want to delete this room preference?")) return;
    try {
      await axios.delete(`${API}/admin/employee-preferences/${employeeNumber}`);
      toast.success("Preference deleted");
      fetchRoomPreferences();
    } catch (error) {
      toast.error("Failed to delete preference");
    }
  };

  // Employee CRUD functions
  const handleSaveEmployee = async () => {
    if (!employeeForm.employee_number || !employeeForm.name) {
      toast.error("Please fill in all fields");
      return;
    }
    
    try {
      if (editingEmployee) {
        // Use id if available, otherwise fallback to employee_number
        const employeeIdentifier = editingEmployee.id || editingEmployee.employee_number;
        await axios.put(`${API}/admin/employees/${employeeIdentifier}`, employeeForm);
        toast.success("Employee updated successfully");
      } else {
        await axios.post(`${API}/admin/employees`, employeeForm);
        toast.success("Employee added successfully");
      }
      setShowEmployeeDialog(false);
      setEditingEmployee(null);
      setEmployeeForm({ employee_number: "", name: "" });
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save employee");
    }
  };

  const handleEditEmployee = (employee) => {
    setEditingEmployee(employee);
    setEmployeeForm({
      employee_number: employee.employee_number,
      name: employee.name
    });
    setShowEmployeeDialog(true);
  };

  const handleDeleteEmployee = async (employeeId) => {
    try {
      await axios.delete(`${API}/admin/employees/${employeeId}`);
      toast.success("Employee deleted successfully");
      setDeleteEmployeeConfirm(null);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete employee");
    }
  };

  // Update booking record (employee number, name, room)
  const handleUpdateRecord = async () => {
    if (!editingRecord || !editingRecord.id) {
      toast.error("No record selected");
      return;
    }
    
    // Validate employee number is numeric only
    if (editingRecord.employee_number && !/^\d*$/.test(editingRecord.employee_number)) {
      toast.error("Employee number must contain only numbers");
      return;
    }
    
    try {
      await axios.put(`${API}/admin/bookings/${editingRecord.id}`, {
        employee_number: editingRecord.employee_number,
        employee_name: editingRecord.employee_name,
        room_number: editingRecord.room_number
      });
      toast.success("Record updated successfully");
      setEditingRecord(null);
      fetchData(startDate || null, endDate || null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update record");
    }
  };

  const handleBulkImport = async () => {
    if (!bulkImportText.trim()) {
      toast.error("Please enter employee data");
      return;
    }
    
    try {
      // Parse CSV/text format: "employee_number,name" or "employee_number name"
      const lines = bulkImportText.trim().split('\n');
      const employeesToImport = lines.map(line => {
        const parts = line.split(/[,\t]/).map(p => p.trim());
        if (parts.length >= 2) {
          return { employee_number: parts[0], name: parts[1] };
        }
        return null;
      }).filter(e => e !== null);
      
      if (employeesToImport.length === 0) {
        toast.error("No valid employee data found. Use format: ID,Name");
        return;
      }
      
      const response = await axios.post(`${API}/admin/employees/bulk`, employeesToImport);
      toast.success(response.data.message);
      setBulkImportText("");
      setShowBulkImport(false);
      fetchEmployees();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import employees");
    }
  };

  const handleCollectFromPortal = async () => {
    setCollectingEmployees(true);
    try {
      toast.info("AI Agent is collecting employees from portal... This may take a minute.");
      const response = await axios.post(`${API}/admin/collect-employees`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchEmployees();
      } else {
        toast.error(response.data.message || "Failed to collect employees");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to collect employees from portal");
    } finally {
      setCollectingEmployees(false);
    }
  };

  const handleCollectDailyFromPortal = async () => {
    if (!employeeScanStartDate || !employeeScanEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    setCollectingEmployees(true);
    try {
      toast.info(`AI Agent scanning portal from ${employeeScanStartDate} to ${employeeScanEndDate}... This may take several minutes.`);
      const response = await axios.post(
        `${API}/admin/collect-employees-daily?start_date=${employeeScanStartDate}&end_date=${employeeScanEndDate}`
      );
      if (response.data.success) {
        toast.success(response.data.message);
        fetchEmployees();
      } else {
        toast.error(response.data.message || "Failed to collect employees");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to collect employees from portal");
    } finally {
      setCollectingEmployees(false);
    }
  };

  const handleImportFromGuests = async () => {
    try {
      toast.info("Importing employees from your guest records...");
      const response = await axios.post(`${API}/admin/import-from-guests`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchEmployees();
      } else {
        toast.error(response.data.message || "Failed to import employees");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import from guest records");
    }
  };

  const fetchSettings = async () => {
    try {
      const [settingsRes, syncStatusRes] = await Promise.all([
        axios.get(`${API}/admin/settings`),
        axios.get(`${API}/admin/sync/status`)
      ]);
      setPortalSettings({
        api_global_username: settingsRes.data.api_global_username || "",
        api_global_password: "",
        alert_email: settingsRes.data.alert_email || "",
        api_global_password_set: settingsRes.data.api_global_password_set || false,
        voice_enabled: settingsRes.data.voice_enabled !== false,
        voice_volume: settingsRes.data.voice_volume || 1.0,
        voice_speed: settingsRes.data.voice_speed || 0.85,
        telegram_chat_id: settingsRes.data.telegram_chat_id || "",
        public_api_key: settingsRes.data.public_api_key || "",
        nightly_rate: settingsRes.data.nightly_rate || 75.0,
        single_room_rate: settingsRes.data.single_room_rate || 85.0,
        double_room_rate: settingsRes.data.double_room_rate || 95.0,
        sales_tax_rate: settingsRes.data.sales_tax_rate || 0.0,
        chatbot_max_rooms: settingsRes.data.chatbot_max_rooms || 3,
        guaranteed_rooms: settingsRes.data.guaranteed_rooms || 25,
        bitsy_enabled: settingsRes.data.bitsy_enabled !== false,
        bitsy_crew_enabled: settingsRes.data.bitsy_crew_enabled !== false,
        email_reports_enabled: settingsRes.data.email_reports_enabled || false,
        email_smtp_host: settingsRes.data.email_smtp_host || "smtp.zoho.com",
        email_smtp_port: settingsRes.data.email_smtp_port || 587,
        email_sender: settingsRes.data.email_sender || "",
        email_password: "",
        email_password_set: settingsRes.data.email_password_set || false,
        email_recipient: settingsRes.data.email_recipient || "",
        email_report_time: settingsRes.data.email_report_time || "00:00"
      });
      setSyncStatus(syncStatusRes.data);
    } catch (error) {
      console.error("Failed to load settings");
    }
  };

  const handleSaveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.post(`${API}/admin/settings`, {
        api_global_username: portalSettings.api_global_username,
        api_global_password: portalSettings.api_global_password || null,
        alert_email: portalSettings.alert_email,
        telegram_chat_id: portalSettings.telegram_chat_id,
        public_api_key: portalSettings.public_api_key,
        nightly_rate: portalSettings.nightly_rate,
        railroad_room_rate: portalSettings.railroad_room_rate,
        api_commission_rate: portalSettings.api_commission_rate,
        single_room_rate: portalSettings.single_room_rate,
        double_room_rate: portalSettings.double_room_rate,
        sales_tax_rate: portalSettings.sales_tax_rate,
        chatbot_max_rooms: portalSettings.chatbot_max_rooms,
        guaranteed_rooms: portalSettings.guaranteed_rooms,
        bitsy_enabled: portalSettings.bitsy_enabled,
        bitsy_crew_enabled: portalSettings.bitsy_crew_enabled
      });
      toast.success("Settings saved successfully");
      fetchSettings();
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSavingSettings(false);
    }
  };

  const handleTestConnection = async () => {
    setRunningSyncTest(true);
    try {
      const response = await axios.post(`${API}/admin/settings/test-connection`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Connection test failed");
    } finally {
      setRunningSyncTest(false);
    }
  };

  const handleRunSync = async () => {
    try {
      // Build URL with optional date parameter
      let url = `${API}/admin/sync/run`;
      const params = [];
      if (syncTargetDate) {
        params.push(`target_date=${syncTargetDate}`);
      }
      if (syncIncludePrevDay) {
        params.push(`include_prev_day=true`);
      }
      if (params.length > 0) {
        url += `?${params.join('&')}`;
      }
      const response = await axios.post(url);
      const targetDate = response.data.target_date_used || syncTargetDate || 'today';
      const recordCount = response.data.hodler_records_count || 0;
      
      console.log('Sync response:', response.data);
      
      // Show single toast with clear message
      toast.success(`Sync started for ${targetDate}${syncIncludePrevDay ? ' + prev day' : ''} - checking ${recordCount} records`);
      
      // Update status immediately
      setSyncStatus({ 
        ...syncStatus, 
        running: true, 
        progress: `Connecting to portal for ${targetDate}...`
      });
      
      // Poll for status updates
      const pollStatus = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/admin/sync/status`);
          setSyncStatus(statusRes.data);
          
          if (!statusRes.data.running) {
            clearInterval(pollStatus);
            if (statusRes.data.last_results) {
              const results = statusRes.data.last_results;
              const verified = results.verified?.length || 0;
              const noBill = results.no_bill?.length || 0;
              const missing = results.missing_in_hodler?.length || 0;
              toast.success(`Sync completed! ✓${verified} verified, ⊘${noBill} no-bill, ?${missing} missing`);
            }
          }
        } catch (e) {
          clearInterval(pollStatus);
        }
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start sync");
    }
  };

  const handleResetSync = async () => {
    try {
      await axios.post(`${API}/admin/sync/reset`);
      const statusRes = await axios.get(`${API}/admin/sync/status`);
      setSyncStatus(statusRes.data);
      toast.success("Sync status reset");
    } catch (error) {
      toast.error("Failed to reset sync status");
    }
  };

  // Manual Entry functions
  const handleManualEntry = async () => {
    if (!manualEntryForm.employee_id || !manualEntryForm.first_name || !manualEntryForm.last_name || !manualEntryForm.room_number) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setManualEntryLoading(true);
    try {
      const response = await axios.post(`${API}/admin/manual-entry`, manualEntryForm);
      toast.success(response.data.message);
      if (response.data.billing) {
        toast.info(`Billing: ${response.data.billing.nights} nights = $${response.data.billing.total}`);
      }
      setShowManualEntryForm(false);
      setManualEntryForm({
        employee_id: "",
        first_name: "",
        last_name: "",
        room_number: "",
        check_in_date: getCentralToday(),
        check_in_time: "14:00",
        check_out_date: "",
        check_out_time: "11:00",
        is_checked_out: false
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create entry");
    }
    setManualEntryLoading(false);
  };

  const handleExportSignIn = async () => {
    try {
      const response = await axios.get(`${API}/admin/export`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "hodler_inn_sign_in_sheet.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Sign-In Sheet (Excel) exported!");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  // Expected Arrivals functions
  const fetchExpectedArrivals = async () => {
    try {
      const response = await axios.get(`${API}/admin/expected-arrivals`);
      setExpectedArrivals(response.data);
    } catch (error) {
      console.error("Failed to fetch expected arrivals");
    }
  };

  const fetchExpectedCheckouts = async () => {
    try {
      const response = await axios.get(`${API}/admin/expected-checkouts`);
      setExpectedCheckouts(response.data);
    } catch (error) {
      console.error("Failed to fetch expected checkouts");
    }
  };

  const checkCpkcEmails = async () => {
    setCheckingCpkcEmails(true);
    try {
      await axios.post(`${API}/admin/check-cpkc-emails`);
      toast.success("Email check completed!");
      fetchExpectedArrivals();
      fetchExpectedCheckouts();
    } catch (error) {
      toast.error("Failed to check emails");
    }
    setCheckingCpkcEmails(false);
  };

  const deleteExpectedArrival = async (arrivalId) => {
    try {
      await axios.delete(`${API}/admin/expected-arrivals/${arrivalId}`);
      toast.success("Arrival removed");
      fetchExpectedArrivals();
    } catch (error) {
      toast.error("Failed to remove arrival");
    }
  };

  const deleteExpectedCheckout = async (checkoutId) => {
    try {
      await axios.delete(`${API}/admin/expected-checkouts/${checkoutId}`);
      toast.success("Check-out removed");
      fetchExpectedCheckouts();
    } catch (error) {
      toast.error("Failed to remove check-out");
    }
  };

  const markCheckoutCompleted = async (checkoutId) => {
    try {
      await axios.post(`${API}/admin/expected-checkouts/${checkoutId}/completed`);
      toast.success("Check-out marked as completed");
      fetchExpectedCheckouts();
    } catch (error) {
      toast.error("Failed to mark check-out as completed");
    }
  };

  const handleExportSignInPng = async () => {
    try {
      const response = await axios.get(`${API}/admin/export-png`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "hodler_inn_sign_in_sheet.png");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Sign-In Sheet (PNG) exported!");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const handleExportBilling = async () => {
    try {
      let url = `${API}/admin/export-billing`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        responseType: "blob"
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      const filename = startDate || endDate 
        ? `hodler_inn_billing_${startDate || 'start'}_to_${endDate || 'end'}.xlsx`
        : "hodler_inn_billing_report.xlsx";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Billing Report (Excel) exported!");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  const handleExportBillingPng = async () => {
    try {
      let url = `${API}/admin/export-billing-png`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, {
        responseType: "blob"
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      const filename = startDate || endDate 
        ? `hodler_inn_billing_${startDate || 'start'}_to_${endDate || 'end'}.png`
        : "hodler_inn_billing_report.png";
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Billing Report (PNG) exported!");
    } catch (error) {
      toast.error("Failed to export");
    }
  };

  // PDF Export functions
  const handleExportSignInPdf = async () => {
    try {
      let url = `${API}/admin/export-pdf`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "hodler_inn_sign_in_sheet.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Sign-In Sheet (PDF) exported!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  const handleExportBillingPdf = async () => {
    try {
      let url = `${API}/admin/export-billing-pdf`;
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url, { responseType: "blob" });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", "hodler_inn_billing_report.pdf");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success("Billing Report (PDF) exported!");
    } catch (error) {
      toast.error("Failed to export PDF");
    }
  };

  // Room management functions
  const handleCreateRoom = async () => {
    if (!roomForm.room_number) {
      toast.error("Room number is required");
      return;
    }
    try {
      await axios.post(`${API}/admin/rooms`, roomForm);
      toast.success("Room created successfully");
      setShowRoomDialog(false);
      setRoomForm({ room_number: "", room_type: "Standard", floor: "1", notes: "" });
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create room");
    }
  };

  const handleEditRoom = (room) => {
    setEditingRoom(room);
    setRoomForm({
      room_number: room.room_number,
      room_type: room.room_type,
      floor: room.floor,
      notes: room.notes || ""
    });
    setShowRoomDialog(true);
  };

  const handleUpdateRoom = async () => {
    try {
      await axios.put(`${API}/admin/rooms/${editingRoom.id}`, roomForm);
      toast.success("Room updated successfully");
      setShowRoomDialog(false);
      setEditingRoom(null);
      setRoomForm({ room_number: "", room_type: "Standard", floor: "1", notes: "" });
      fetchRooms();
    } catch (error) {
      toast.error("Failed to update room");
    }
  };

  const handleDeleteRoom = async (roomId) => {
    try {
      await axios.delete(`${API}/admin/rooms/${roomId}`);
      toast.success("Room deleted successfully");
      setDeleteRoomConfirm(null);
      fetchRooms();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete room");
    }
  };

  const handleLogout = () => {
    clearAdminToken();
    navigate("/admin");
  };

  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
    otp: ""
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);

  const handleRequestPasswordChangeOTP = async () => {
    setSendingOtp(true);
    try {
      const response = await axios.post(`${API}/admin/request-otp?purpose=password_change`);
      toast.success(response.data.message);
      setOtpSent(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally {
      setSendingOtp(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.otp) {
      toast.error("Please fill in all fields including OTP");
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords don't match");
      return;
    }
    if (passwordForm.newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (passwordForm.otp.length !== 6) {
      toast.error("OTP must be 6 digits");
      return;
    }

    setChangingPassword(true);
    try {
      const token = sessionStorage.getItem("adminToken");
      await axios.post(`${API}/admin/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword,
        otp: passwordForm.otp
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully!");
      setShowPasswordChange(false);
      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "", otp: "" });
      setOtpSent(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setEditForm({
      employee_number: record.employee_number || "",
      employee_name: record.employee_name || "",
      room_number: record.room_number,
      check_in_date: record.check_in_date,
      check_in_time: record.check_in_time,
      check_out_date: record.check_out_date || "",
      check_out_time: record.check_out_time || ""
    });
  };

  const handleSaveEdit = async () => {
    try {
      // Validate employee number is numeric only
      if (editForm.employee_number && !/^\d*$/.test(editForm.employee_number)) {
        toast.error("Employee number must contain only numbers");
        return;
      }
      await axios.put(`${API}/admin/bookings/${editingRecord.id}`, editForm);
      toast.success("Record updated successfully");
      setEditingRecord(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to update record");
    }
  };

  const handleDelete = async (bookingId) => {
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}`);
      toast.success("Record deleted successfully");
      setDeleteConfirm(null);
      fetchData();
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  if (contextLoading) {
    return (
      <div className="min-h-screen bg-vault-bg flex items-center justify-center">
        <div className="text-vault-gold font-mono">Loading dashboard...</div>
      </div>
    );
  }

  // Debug: Show a simple test render first
  console.log('[AdminDashboard] About to render main content, activeView:', activeView);

  return (
    <div className="min-h-screen bg-vault-bg grid-bg" data-testid="admin-dashboard">
      {/* Sidebar - Hidden on mobile */}
      <div className="fixed left-0 top-0 h-full w-60 admin-sidebar p-6 flex-col hidden md:flex overflow-hidden">
        {/* Logo */}
        <div className="mb-6 flex-shrink-0">
          <img 
            src="/hodler-logo.png" 
            alt="Hodler Inn" 
            className="h-12 object-contain"
          />
          <p className="font-mono text-[9px] text-vault-gold uppercase tracking-widest mt-2">Admin Panel</p>
        </div>

        {/* Navigation */}
        <nav className="space-y-1 flex-1 overflow-y-auto pr-1 scrollbar-thin">
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'dashboard' ? 'active' : ''}`}
            onClick={() => handleViewChange('dashboard')}
          >
            <LayoutDashboard className="w-4 h-4" />
            <span className="font-manrope text-sm">Dashboard</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'signin' ? 'active' : ''}`}
            onClick={() => handleViewChange('signin')}
            data-testid="nav-signin-view-btn"
          >
            <ClipboardList className="w-4 h-4" />
            <span className="font-manrope text-sm">Sign-In Sheet</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'billing' ? 'active' : ''}`}
            onClick={() => handleViewChange('billing')}
            data-testid="nav-billing-view-btn"
          >
            <Receipt className="w-4 h-4" />
            <span className="font-manrope text-sm">Billing Report</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'rooms' ? 'active' : ''}`}
            onClick={() => handleViewChange('rooms')}
            data-testid="nav-rooms-view-btn"
          >
            <Bed className="w-4 h-4" />
            <span className="font-manrope text-sm">Room Management</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'employees' ? 'active' : ''}`}
            onClick={() => handleViewChange('employees')}
            data-testid="nav-employees-view-btn"
          >
            <Users className="w-4 h-4" />
            <span className="font-manrope text-sm">Employee List</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'messages' ? 'active' : ''}`}
            onClick={() => handleViewChange('messages')}
            data-testid="nav-messages-view-btn"
          >
            <MessageSquare className="w-4 h-4" />
            <span className="font-manrope text-sm">Employee Messages</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'room-preferences' ? 'active' : ''}`}
            onClick={() => handleViewChange('room-preferences')}
            data-testid="nav-room-preferences-view-btn"
          >
            <Star className="w-4 h-4" />
            <span className="font-manrope text-sm">Room Preferences</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'guests' ? 'active' : ''}`}
            onClick={() => handleViewChange('guests')}
            data-testid="nav-guests-view-btn"
          >
            <UserCheck className="w-4 h-4" />
            <span className="font-manrope text-sm">Guest Verification</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'guarantee' ? 'active' : ''}`}
            onClick={() => handleViewChange('guarantee')}
            data-testid="nav-guarantee-view-btn"
          >
            <FileBarChart className="w-4 h-4" />
            <span className="font-manrope text-sm">Guarantee Report</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'crypto' ? 'active' : ''}`}
            onClick={() => handleViewChange('crypto')}
            data-testid="nav-crypto-view-btn"
          >
            <Bitcoin className="w-4 h-4" />
            <span className="font-manrope text-sm">Crypto Payments</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'profitability' ? 'active' : ''}`}
            onClick={() => handleViewChange('profitability')}
            data-testid="nav-profitability-view-btn"
          >
            <TrendingUp className="w-4 h-4" />
            <span className="font-manrope text-sm">Profitability</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'pest-control' ? 'active' : ''}`}
            onClick={() => handleViewChange('pest-control')}
            data-testid="nav-pest-control-btn"
          >
            <Bug className="w-4 h-4" />
            <span className="font-manrope text-sm">Pest Control</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'bedding' ? 'active' : ''}`}
            onClick={() => handleViewChange('bedding')}
            data-testid="nav-bedding-btn"
          >
            <Bed className="w-4 h-4" />
            <span className="font-manrope text-sm">Bedding Wash</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'settings' ? 'active' : ''}`}
            onClick={() => handleViewChange('settings')}
            data-testid="nav-settings-view-btn"
          >
            <Settings className="w-4 h-4" />
            <span className="font-manrope text-sm">Portal Settings</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'forensic' ? 'active' : ''}`}
            onClick={() => handleViewChange('forensic')}
            data-testid="nav-forensic-view-btn"
          >
            <Shield className="w-4 h-4" />
            <span className="font-manrope text-sm">Forensic Reports</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'crypto-settings' ? 'active' : ''}`}
            onClick={() => handleViewChange('crypto-settings')}
            data-testid="nav-crypto-settings-btn"
          >
            <Wallet className="w-4 h-4" />
            <span className="font-manrope text-sm">Crypto Settings</span>
          </div>
          <div 
            className={`admin-nav-item cursor-pointer ${activeView === 'notices' ? 'active' : ''}`}
            onClick={() => handleViewChange('notices')}
            data-testid="nav-notices-btn"
          >
            <AlertTriangle className="w-4 h-4" />
            <span className="font-manrope text-sm">Active Notices</span>
          </div>
          <div 
            className="admin-nav-item cursor-pointer"
            onClick={() => navigate('/admin/docs')}
            data-testid="nav-docs-view-btn"
          >
            <FileText className="w-4 h-4" />
            <span className="font-manrope text-sm">Documentation</span>
          </div>
          <div 
            className="admin-nav-item cursor-pointer"
            onClick={() => window.open('/#/qrcodes', '_blank')}
            data-testid="nav-qrcodes-btn"
          >
            <QrCode className="w-4 h-4" />
            <span className="font-manrope text-sm">Print QR Codes</span>
          </div>
        </nav>

        {/* DB Health indicator — shows green pill when healthy, amber/red when Atlas is glitching */}
        <div className="px-4 pb-3">
          <DbHealthBanner API={API} />
        </div>

        {/* Logout */}
        <button 
          onClick={handleLogout}
          className="admin-nav-item mt-auto flex-shrink-0 text-vault-error hover:text-red-400"
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4" />
          <span className="font-manrope text-sm">Logout</span>
        </button>
      </div>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 bg-vault-surface border-b border-vault-border p-4 z-10">
        <div className="flex items-center justify-between">
          <img 
            src="/hodler-logo.png" 
            alt="Hodler Inn" 
            className="h-8 object-contain"
          />
          <div className="flex items-center gap-2">
            <DbHealthBanner API={API} />
            <button 
              onClick={handleLogout}
              className="text-vault-text-secondary hover:text-vault-gold"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </div>
        {/* Mobile Nav Tabs */}
        <div className="flex gap-2 mt-3 overflow-x-auto">
          <button 
            onClick={() => handleViewChange('dashboard')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'dashboard' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Dashboard
          </button>
          <button 
            onClick={() => handleViewChange('signin')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'signin' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Sign-In Sheet
          </button>
          <button 
            onClick={() => handleViewChange('billing')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'billing' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Billing Report
          </button>
          <button 
            onClick={() => handleViewChange('rooms')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'rooms' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Rooms
          </button>
          <button 
            onClick={() => handleViewChange('guarantee')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'guarantee' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Guarantee
          </button>
          <button 
            onClick={() => handleViewChange('crypto')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'crypto' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Crypto
          </button>
          <button 
            onClick={() => handleViewChange('profitability')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'profitability' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Profit
          </button>
          <button 
            onClick={() => handleViewChange('pest-control')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'pest-control' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Pest
          </button>
          <button 
            onClick={() => handleViewChange('bedding')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'bedding' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Bedding
          </button>
          <button 
            onClick={() => handleViewChange('notices')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'notices' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Notices
          </button>
          <button 
            onClick={() => handleViewChange('settings')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'settings' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Settings
          </button>
          <button 
            onClick={() => handleViewChange('forensic')}
            className={`px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap ${activeView === 'forensic' ? 'bg-vault-gold text-black' : 'bg-vault-surface-highlight text-vault-text-secondary'}`}
          >
            Forensic
          </button>
          <button 
            onClick={() => navigate('/admin/docs')}
            className="px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap bg-vault-surface-highlight text-vault-text-secondary"
          >
            Docs
          </button>
          <button 
            onClick={() => window.open('/#/qrcodes', '_blank')}
            className="px-3 py-1.5 rounded text-xs font-medium whitespace-nowrap bg-vault-surface-highlight text-vault-text-secondary flex items-center gap-1"
          >
            <Printer className="w-3 h-3" />
            QR Codes
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="md:ml-60 p-4 md:p-8 pt-28 md:pt-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          {/* Dashboard View */}
          {/* Dashboard View */}
          {activeView === 'dashboard' && (
            <DashboardTab />
          )}

          {/* Sign-In Sheet View */}
          {activeView === 'signin' && (
            <SignInTab />
          )}

          {/* Billing Report View */}
          {activeView === 'billing' && (
            <BillingTab />
          )}

          {/* Room Management View */}
          {activeView === 'rooms' && (
            <RoomsTab
              rooms={rooms} fetchRooms={fetchRooms}
              showRoomDialog={showRoomDialog} setShowRoomDialog={setShowRoomDialog}
              editingRoom={editingRoom} setEditingRoom={setEditingRoom}
              roomForm={roomForm} setRoomForm={setRoomForm}
              roomSortOrder={roomSortOrder} setRoomSortOrder={setRoomSortOrder}
              deleteRoomConfirm={deleteRoomConfirm} setDeleteRoomConfirm={setDeleteRoomConfirm}
              blockedRooms={blockedRooms} blockRoomForm={blockRoomForm} setBlockRoomForm={setBlockRoomForm}
              showBlockRoomDialog={showBlockRoomDialog} setShowBlockRoomDialog={setShowBlockRoomDialog}
              blockedRoomStats={blockedRoomStats}
              reservations={reservations} bitsyBookings={bitsyBookings}
              showBitsyBookings={showBitsyBookings} setShowBitsyBookings={setShowBitsyBookings}
              editingBitsyBooking={editingBitsyBooking} setEditingBitsyBooking={setEditingBitsyBooking}
              bitsyEditForm={bitsyEditForm} setBitsyEditForm={setBitsyEditForm}
              nonRailroadHistory={nonRailroadHistory} showNonRailroadHistory={showNonRailroadHistory} setShowNonRailroadHistory={setShowNonRailroadHistory}
              checkingInBitsyBooking={checkingInBitsyBooking} setCheckingInBitsyBooking={setCheckingInBitsyBooking}
              bitsyCheckInRoom={bitsyCheckInRoom} setBitsyCheckInRoom={setBitsyCheckInRoom}
              showMultiRoomBooking={showMultiRoomBooking} setShowMultiRoomBooking={setShowMultiRoomBooking}
              multiRoomForm={multiRoomForm} setMultiRoomForm={setMultiRoomForm}
              selectedRooms={selectedRooms} setSelectedRooms={setSelectedRooms}
              multiRoomLoading={multiRoomLoading}
              lastConfirmation={lastConfirmation} setLastConfirmation={setLastConfirmation}
              blockedGuests={blockedGuests} showBlockedGuests={showBlockedGuests} setShowBlockedGuests={setShowBlockedGuests}
              blockGuestDialog={blockGuestDialog} setBlockGuestDialog={setBlockGuestDialog}
              blockGuestForm={blockGuestForm} setBlockGuestForm={setBlockGuestForm}
              showEditGuestDialog={showEditGuestDialog} editGuestForm={editGuestForm} setEditGuestForm={setEditGuestForm}
              checkinDialog={checkinDialog} setCheckinDialog={setCheckinDialog}
              handleCreateRoom={handleCreateRoom} handleEditRoom={handleEditRoom}
              handleUpdateRoom={handleUpdateRoom} handleDeleteRoom={handleDeleteRoom}
              handleBlockRoom={handleBlockRoom} handleUnblockRoom={handleUnblockRoom}
              handleMarkRoomDirty={handleMarkRoomDirty} handleMarkRoomClean={handleMarkRoomClean}
              fetchBlockedRooms={fetchBlockedRooms} fetchBlockedRoomStats={fetchBlockedRoomStats}
              fetchReservations={fetchReservations} fetchBitsyBookings={fetchBitsyBookings}
              handleEditBitsyBooking={handleEditBitsyBooking} handleSaveBitsyBooking={handleSaveBitsyBooking}
              handleCancelBitsyBooking={handleCancelBitsyBooking} handleCheckInBitsyBooking={handleCheckInBitsyBooking}
              fetchNonRailroadHistory={fetchNonRailroadHistory}
              handleEditGuest={handleEditGuest} handleSaveGuestEdit={handleSaveGuestEdit}
              handleBlockGuest={handleBlockGuest} handleUnblockGuest={handleUnblockGuest}
              handleQuickBlockGuest={handleQuickBlockGuest}
              addRoomToBooking={addRoomToBooking} removeRoomFromBooking={removeRoomFromBooking}
              updateRoomInBooking={updateRoomInBooking} handleMultiRoomBooking={handleMultiRoomBooking}
              copyConfirmationToClipboard={copyConfirmationToClipboard}
              openCheckinDialog={openCheckinDialog} handleCheckinReservation={handleCheckinReservation}
              handleCancelReservation={handleCancelReservation} handleConfirmReservation={handleConfirmReservation}
              handleSendConfirmationEmail={handleSendConfirmationEmail}
              fetchBlockedGuests={fetchBlockedGuests} fetchStats={fetchData}
            />
          )}

          {/* Employees View */}
          {activeView === 'employees' && (
            <EmployeesTab />
          )}

          {/* Employee Messages View */}
          {activeView === 'messages' && (
            <MessagesTab />
          )}

          {/* Room Preferences View */}
          {activeView === 'room-preferences' && (
            <RoomPreferencesTab />
          )}

          {/* Guest Verification View */}
          {activeView === 'guests' && (
            <GuestsTab />
          )}

          {/* Guarantee Report View */}
          {activeView === 'guarantee' && (
            <GuaranteeTab />
          )}

          {/* Crypto Payments View */}
          {activeView === 'crypto' && (
            <CryptoTab />
          )}

          {/* Profitability View */}
          {activeView === 'profitability' && (
            <ProfitabilityTab portalSettings={portalSettings} />
          )}

          {/* Pest Control View */}
          {activeView === 'pest-control' && (
            <PestControlTab rooms={rooms} />
          )}

          {/* Bedding Wash View */}
          {activeView === 'bedding' && (
            <BeddingTab rooms={rooms} />
          )}

          {/* Settings View */}
          {activeView === 'settings' && (
            <SettingsTab
              portalSettings={portalSettings} setPortalSettings={setPortalSettings}
              savingSettings={savingSettings} setSavingSettings={setSavingSettings}
              emailAlertSettings={emailAlertSettings} setEmailAlertSettings={setEmailAlertSettings}
              newRecipientEmail={newRecipientEmail} setNewRecipientEmail={setNewRecipientEmail}
              emailPreview={emailPreview} setEmailPreview={setEmailPreview}
              previewLoading={previewLoading} setPreviewLoading={setPreviewLoading}
              telegramSettings={telegramSettings} setTelegramSettings={setTelegramSettings}
              telegramLoading={telegramLoading} setTelegramLoading={setTelegramLoading}
              telegramTestingChannel={telegramTestingChannel} setTelegramTestingChannel={setTelegramTestingChannel}
              discoveredGroups={discoveredGroups} setDiscoveredGroups={setDiscoveredGroups}
              discoverGroupsLoading={discoverGroupsLoading} setDiscoverGroupsLoading={setDiscoverGroupsLoading}
              showGroupSelector={showGroupSelector} setShowGroupSelector={setShowGroupSelector}
              selectedChannelForGroup={selectedChannelForGroup} setSelectedChannelForGroup={setSelectedChannelForGroup}
              syncStatus={syncStatus} setSyncStatus={setSyncStatus}
              runningSyncTest={runningSyncTest} setRunningSyncTest={setRunningSyncTest}
              syncTargetDate={syncTargetDate} setSyncTargetDate={setSyncTargetDate}
              syncIncludePrevDay={syncIncludePrevDay} setSyncIncludePrevDay={setSyncIncludePrevDay}
              markingNoBill={markingNoBill} setMarkingNoBill={setMarkingNoBill}
              testingEmail={testingEmail} setTestingEmail={setTestingEmail}
              testingZoho={testingZoho} setTestingZoho={setTestingZoho}
              uploadingZoho={uploadingZoho} setUploadingZoho={setUploadingZoho}
              zohoUploadDate={zohoUploadDate} setZohoUploadDate={setZohoUploadDate}
              backingUpDatabase={backingUpDatabase} setBackingUpDatabase={setBackingUpDatabase}
              lastBackupResult={lastBackupResult} setLastBackupResult={setLastBackupResult}
              showPasswordChange={showPasswordChange} setShowPasswordChange={setShowPasswordChange}
              passwordForm={passwordForm} setPasswordForm={setPasswordForm}
              changingPassword={changingPassword} setChangingPassword={setChangingPassword}
              otpSent={otpSent} setOtpSent={setOtpSent}
              sendingOtp={sendingOtp} setSendingOtp={setSendingOtp}
              fetchSettings={fetchSettings} handleSaveSettings={handleSaveSettings}
              handleTestConnection={handleTestConnection} handleRunSync={handleRunSync}
              handleResetSync={handleResetSync} handleMarkNoBill={handleMarkNoBill}
              saveTelegramSettings={saveTelegramSettings} testTelegramChannel={testTelegramChannel}
              updateTelegramChannel={updateTelegramChannel}
              discoverTelegramGroups={discoverTelegramGroups} selectGroupForChannel={selectGroupForChannel}
              fetchEmailAlertSettings={fetchEmailAlertSettings} updateEmailAlertSettings={updateEmailAlertSettings}
              addEmailRecipient={addEmailRecipient} removeEmailRecipient={removeEmailRecipient}
              toggleRecipientAlert={toggleRecipientAlert} sendTestAlert={sendTestAlert}
              previewEmailAlert={previewEmailAlert}
              getRecipientAlerts={getRecipientAlerts} getRecipientEmail={getRecipientEmail}
              handleRequestPasswordChangeOTP={handleRequestPasswordChangeOTP}
              handleChangePassword={handleChangePassword}
              rooms={rooms}
            />
          )}

          {/* Forensic View */}
          {activeView === 'forensic' && (
            <ForensicIncidentsView API={API} rooms={rooms} />
          )}

          {/* Crypto Settings View */}
          {activeView === 'crypto-settings' && (
            <CryptoSettingsView API={API} />
          )}

          {/* Active Notices View */}
          {activeView === 'notices' && (
            <ActiveNoticesView API={API} />
          )}
        </motion.div>
      </div>
      
      {/* Edit Dialog */}
      {editingRecord && (
        <Dialog open={!!editingRecord} onOpenChange={() => setEditingRecord(null)}>
          <DialogContent className="bg-vault-surface border-vault-border">
            <DialogHeader>
              <DialogTitle className="font-outfit text-vault-text">
                Edit Record - {editingRecord.employee_name}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Room Number</label>
                <Input
                  value={editForm.room_number}
                  onChange={(e) => setEditForm({...editForm, room_number: e.target.value})}
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-In Date</label>
                  <Input
                    type="date"
                    value={editForm.check_in_date}
                    onChange={(e) => setEditForm({...editForm, check_in_date: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-In Time</label>
                  <Input
                    type="time"
                    value={editForm.check_in_time}
                    onChange={(e) => setEditForm({...editForm, check_in_time: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-Out Date</label>
                  <Input
                    type="date"
                    value={editForm.check_out_date}
                    onChange={(e) => setEditForm({...editForm, check_out_date: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-Out Time</label>
                  <Input
                    type="time"
                    value={editForm.check_out_time}
                    onChange={(e) => setEditForm({...editForm, check_out_time: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditingRecord(null)} className="text-vault-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleSaveEdit} className="vault-btn-primary">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <Dialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
          <DialogContent className="bg-vault-surface border-vault-border">
            <DialogHeader>
              <DialogTitle className="font-outfit text-vault-text">
                Delete Record
              </DialogTitle>
            </DialogHeader>
            <p className="text-vault-text-secondary py-4">
              Are you sure you want to delete the record for <span className="text-vault-gold font-medium">{deleteConfirm.employee_name}</span> in room <span className="text-vault-gold font-medium">{deleteConfirm.room_number}</span>?
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteConfirm(null)} className="text-vault-text-secondary">
                Cancel
              </Button>
              <Button onClick={() => handleDelete(deleteConfirm.id)} className="bg-red-600 hover:bg-red-700 text-white">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Non-Railroad Guest Dialog */}
      {showEditGuestDialog && (
        <Dialog open={showEditGuestDialog} onOpenChange={setShowEditGuestDialog}>
          <DialogContent className="bg-vault-surface border-vault-border max-w-md">
            <DialogHeader>
              <DialogTitle className="font-outfit text-vault-text flex items-center gap-2">
                <Edit className="w-5 h-5 text-amber-400" />
                Edit Guest Information
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Room Number</label>
                  <Input
                    value={editGuestForm.room_number}
                    onChange={(e) => setEditGuestForm({...editGuestForm, room_number: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Rate/Night ($)</label>
                  <Input
                    type="number"
                    value={editGuestForm.room_rate}
                    onChange={(e) => setEditGuestForm({...editGuestForm, room_rate: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Guest Name *</label>
                <Input
                  value={editGuestForm.guest_name}
                  onChange={(e) => setEditGuestForm({...editGuestForm, guest_name: e.target.value})}
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={editGuestForm.email}
                    onChange={(e) => setEditGuestForm({...editGuestForm, email: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Phone</label>
                  <Input
                    value={editGuestForm.phone}
                    onChange={(e) => setEditGuestForm({...editGuestForm, phone: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-In Date</label>
                  <Input
                    type="date"
                    value={editGuestForm.check_in_date}
                    onChange={(e) => setEditGuestForm({...editGuestForm, check_in_date: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-Out Date</label>
                  <Input
                    type="date"
                    value={editGuestForm.check_out_date}
                    onChange={(e) => setEditGuestForm({...editGuestForm, check_out_date: e.target.value})}
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Notes</label>
                <Input
                  value={editGuestForm.notes}
                  onChange={(e) => setEditGuestForm({...editGuestForm, notes: e.target.value})}
                  placeholder="Any special notes..."
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowEditGuestDialog(false)} className="text-vault-text-secondary">
                Cancel
              </Button>
              <Button onClick={handleSaveGuestEdit} className="bg-amber-600 hover:bg-amber-700 text-white">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Room Add/Edit Dialog */}
      {showRoomDialog && (
        <Dialog open={showRoomDialog} onOpenChange={() => { setShowRoomDialog(false); setEditingRoom(null); }}>
          <DialogContent className="bg-vault-surface border-vault-border">
            <DialogHeader>
              <DialogTitle className="font-outfit text-vault-text">
                {editingRoom ? 'Edit Room' : 'Add New Room'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Room Number *</label>
                <Input
                  value={roomForm.room_number}
                  onChange={(e) => setRoomForm({...roomForm, room_number: e.target.value})}
                  placeholder="e.g., 101, 102A"
                  className="bg-black/50 border-vault-border text-vault-text"
                  data-testid="room-number-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Room Type</label>
                  <Select value={roomForm.room_type} onValueChange={(v) => setRoomForm({...roomForm, room_type: v})}>
                    <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-vault-surface border-vault-border">
                      <SelectItem value="Standard">Standard</SelectItem>
                      <SelectItem value="Deluxe">Deluxe</SelectItem>
                      <SelectItem value="Suite">Suite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Floor</label>
                  <Select value={roomForm.floor} onValueChange={(v) => setRoomForm({...roomForm, floor: v})}>
                    <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-vault-surface border-vault-border">
                      <SelectItem value="1">Floor 1</SelectItem>
                      <SelectItem value="2">Floor 2</SelectItem>
                      <SelectItem value="3">Floor 3</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {editingRoom && (
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Status</label>
                  <Select value={roomForm.status || editingRoom.status} onValueChange={(v) => setRoomForm({...roomForm, status: v})}>
                    <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-vault-surface border-vault-border">
                      <SelectItem value="available">Available</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Notes</label>
                <Input
                  value={roomForm.notes}
                  onChange={(e) => setRoomForm({...roomForm, notes: e.target.value})}
                  placeholder="Optional notes about the room"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => { setShowRoomDialog(false); setEditingRoom(null); }} className="text-vault-text-secondary">
                Cancel
              </Button>
              <Button 
                onClick={editingRoom ? handleUpdateRoom : handleCreateRoom} 
                className="vault-btn-primary"
                data-testid="save-room-btn"
              >
                {editingRoom ? 'Save Changes' : 'Add Room'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Room Confirmation Dialog */}
      {deleteRoomConfirm && (
        <Dialog open={!!deleteRoomConfirm} onOpenChange={() => setDeleteRoomConfirm(null)}>
          <DialogContent className="bg-vault-surface border-vault-border">
            <DialogHeader>
              <DialogTitle className="font-outfit text-vault-text">
                Delete Room
              </DialogTitle>
            </DialogHeader>
            <p className="text-vault-text-secondary py-4">
              Are you sure you want to delete <span className="text-vault-gold font-medium">Room {deleteRoomConfirm.room_number}</span>? This action cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDeleteRoomConfirm(null)} className="text-vault-text-secondary">
                Cancel
              </Button>
              <Button onClick={() => handleDeleteRoom(deleteRoomConfirm.id)} className="bg-red-600 hover:bg-red-700 text-white">
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Block Room Dialog (Other Guest) */}
      {showBlockRoomDialog && (
        <Dialog open={showBlockRoomDialog} onOpenChange={() => setShowBlockRoomDialog(false)}>
          <DialogContent className="bg-vault-surface border-vault-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-outfit text-vault-text flex items-center gap-2">
                <Users className="w-5 h-5 text-amber-400" />
                Non-Railroad Guest Booking
              </DialogTitle>
            </DialogHeader>
            <p className="text-vault-text-secondary text-sm mb-4">
              Check in or make a reservation for non-railroad guests.
            </p>
            <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
              {/* SECTION 1: DATES */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-In Date *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal bg-black/50 border-vault-border text-vault-text hover:bg-black/70"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {blockRoomForm.check_in_date ? format(new Date(blockRoomForm.check_in_date + 'T00:00:00'), 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-vault-surface border-vault-border" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={blockRoomForm.check_in_date ? new Date(blockRoomForm.check_in_date + 'T00:00:00') : undefined}
                        onSelect={(date) => setBlockRoomForm({...blockRoomForm, check_in_date: date ? format(date, 'yyyy-MM-dd') : ''})}
                        initialFocus
                        className="text-vault-text"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-Out Date *</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal bg-black/50 border-vault-border text-vault-text hover:bg-black/70"
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        {blockRoomForm.check_out_date ? format(new Date(blockRoomForm.check_out_date + 'T00:00:00'), 'PPP') : 'Select date'}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-vault-surface border-vault-border" align="start">
                      <CalendarComponent
                        mode="single"
                        selected={blockRoomForm.check_out_date ? new Date(blockRoomForm.check_out_date + 'T00:00:00') : undefined}
                        onSelect={(date) => setBlockRoomForm({...blockRoomForm, check_out_date: date ? format(date, 'yyyy-MM-dd') : ''})}
                        disabled={(date) => blockRoomForm.check_in_date && date < new Date(blockRoomForm.check_in_date + 'T00:00:00')}
                        initialFocus
                        className="text-vault-text"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Same-day reservation toggle */}
              {blockRoomForm.check_in_date === getCentralToday() && (
                <div className="flex items-center gap-3 bg-blue-900/20 border border-blue-500/30 rounded-lg p-3">
                  <input
                    type="checkbox"
                    id="sameday-reservation"
                    checked={blockRoomForm.is_reservation || false}
                    onChange={(e) => setBlockRoomForm({...blockRoomForm, is_reservation: e.target.checked})}
                    className="w-4 h-4"
                    data-testid="sameday-reservation-toggle"
                  />
                  <label htmlFor="sameday-reservation" className="text-blue-300 text-sm">
                    <strong>Reserve for later today</strong> — Guest hasn't arrived yet, save as reservation (no room assignment needed)
                  </label>
                </div>
              )}

              {/* SECTION 2: ROOM TYPE AND RATE */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Room Number</label>
                  <Select 
                    value={blockRoomForm.room_number} 
                    onValueChange={(val) => {
                      const selectedRoom = rooms.find(r => r.room_number === val);
                      const autoRate = selectedRoom?.room_type === 'Two Bed' 
                        ? portalSettings.double_room_rate 
                        : portalSettings.single_room_rate;
                      setBlockRoomForm({...blockRoomForm, room_number: val, room_rate: String(autoRate)});
                    }}
                  >
                    <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                      <SelectValue placeholder="Select room" />
                    </SelectTrigger>
                    <SelectContent className="bg-vault-surface border-vault-border">
                      {rooms
                        .filter(r => r.status === 'available' && !blockedRooms.some(b => b.room_number === r.room_number))
                        .map(room => (
                          <SelectItem key={room.id} value={room.room_number} className="text-vault-text">
                            Room {room.room_number} ({room.room_type})
                          </SelectItem>
                        ))
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Or Room Type (for reservation)</label>
                  <Select 
                    value={blockRoomForm.room_type} 
                    onValueChange={(val) => {
                      const autoRate = val === 'Two Bed' 
                        ? portalSettings.double_room_rate 
                        : portalSettings.single_room_rate;
                      setBlockRoomForm({...blockRoomForm, room_type: val, room_rate: String(autoRate)});
                    }}
                    disabled={!!blockRoomForm.room_number}
                  >
                    <SelectTrigger className="bg-black/50 border-vault-border text-vault-text">
                      <SelectValue placeholder="Select room type" />
                    </SelectTrigger>
                    <SelectContent className="bg-vault-surface border-vault-border">
                      <SelectItem value="Single Bed" className="text-vault-text">Single Bed (${portalSettings.single_room_rate}/night)</SelectItem>
                      <SelectItem value="Two Bed" className="text-vault-text">Two Bed (${portalSettings.double_room_rate}/night)</SelectItem>
                      <SelectItem value="Suite" className="text-vault-text">Suite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Room Rate ($/night) — editable</label>
                <Input
                  type="number"
                  step="0.01"
                  value={blockRoomForm.room_rate}
                  onChange={(e) => setBlockRoomForm({...blockRoomForm, room_rate: e.target.value})}
                  placeholder="0.00"
                  className="bg-black/50 border-vault-border text-vault-text"
                  data-testid="block-room-rate-input"
                />
              </div>
              
              {/* Cost breakdown with tax */}
              {blockRoomForm.room_rate && blockRoomForm.check_in_date && blockRoomForm.check_out_date && (() => {
                const rate = parseFloat(blockRoomForm.room_rate) || 0;
                const taxPct = portalSettings.sales_tax_rate || 10.3;
                const taxPerNight = Math.round(rate * (taxPct / 100) * 100) / 100;
                const totalPerNight = rate + taxPerNight;
                const nights = Math.max(0, Math.ceil((new Date(blockRoomForm.check_out_date) - new Date(blockRoomForm.check_in_date)) / (1000 * 60 * 60 * 24)));
                const subtotal = rate * nights;
                const totalTax = taxPerNight * nights;
                const grandTotal = totalPerNight * nights;
                return (
                  <div className="bg-vault-gold/10 border border-vault-gold/30 rounded-lg p-3 space-y-1" data-testid="block-cost-breakdown">
                    <div className="flex justify-between text-sm">
                      <span className="text-vault-text-secondary">Room Rate:</span>
                      <span className="text-vault-text">${rate.toFixed(2)}/night</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-vault-text-secondary">Sales Tax ({taxPct}%):</span>
                      <span className="text-vault-text">${taxPerNight.toFixed(2)}/night</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-vault-gold/20 pt-1">
                      <span className="text-vault-text-secondary">Total/Night:</span>
                      <span className="text-vault-text font-medium">${totalPerNight.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-vault-text-secondary">Nights:</span>
                      <span className="text-vault-text">{nights}</span>
                    </div>
                    <div className="flex justify-between text-sm border-t border-vault-gold/30 pt-1 mt-1">
                      <span className="text-vault-text-secondary">Subtotal (Rooms):</span>
                      <span className="text-vault-text">${subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-vault-text-secondary">Total Tax:</span>
                      <span className="text-vault-text">${totalTax.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold border-t border-vault-gold/30 pt-1 mt-1">
                      <span className="text-vault-gold">Grand Total:</span>
                      <span className="text-vault-gold text-lg">${grandTotal.toFixed(2)}</span>
                    </div>
                  </div>
                );
              })()}

              {/* SECTION 3: GUEST INFO */}
              <div className="border-t border-vault-border pt-4 mt-2">
                <p className="text-xs text-vault-text-secondary mb-3 uppercase tracking-wider">Guest Information</p>
              </div>
              
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Guest Name *</label>
                <Input
                  value={blockRoomForm.guest_name}
                  onChange={(e) => setBlockRoomForm({...blockRoomForm, guest_name: e.target.value})}
                  placeholder="Full name"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Address</label>
                <Input
                  value={blockRoomForm.address}
                  onChange={(e) => setBlockRoomForm({...blockRoomForm, address: e.target.value})}
                  placeholder="Street, City, State, ZIP"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Email</label>
                  <Input
                    type="email"
                    value={blockRoomForm.email}
                    onChange={(e) => setBlockRoomForm({...blockRoomForm, email: e.target.value})}
                    placeholder="guest@email.com"
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
                <div>
                  <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Phone Number</label>
                  <Input
                    value={blockRoomForm.phone}
                    onChange={(e) => setBlockRoomForm({...blockRoomForm, phone: e.target.value})}
                    placeholder="(xxx) xxx-xxxx"
                    className="bg-black/50 border-vault-border text-vault-text"
                  />
                </div>
              </div>
              
              {/* SECTION 4: NOTES (at the end) */}
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Notes</label>
                <Input
                  value={blockRoomForm.notes}
                  onChange={(e) => setBlockRoomForm({...blockRoomForm, notes: e.target.value})}
                  placeholder="Payment method, special requests, etc."
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              
              {blockRoomForm.check_in_date > getCentralToday() && !blockRoomForm.is_reservation && (
                <div className="bg-blue-900/30 border border-blue-500/30 rounded-lg p-3">
                  <p className="text-blue-400 text-sm">
                    This is a <strong>future reservation</strong>. Guest will need to check in on arrival.
                  </p>
                </div>
              )}
              
              {/* Send Confirmation Email Option */}
              {blockRoomForm.email && blockRoomForm.check_in_date > getCentralToday() && (
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="sendConfirmationEmail"
                    checked={blockRoomForm.send_confirmation_email || false}
                    onChange={(e) => setBlockRoomForm({...blockRoomForm, send_confirmation_email: e.target.checked})}
                    className="w-4 h-4"
                  />
                  <label htmlFor="sendConfirmationEmail" className="text-vault-text-secondary text-sm">
                    Send confirmation email to {blockRoomForm.email}
                  </label>
                </div>
              )}
            </div>
            <DialogFooter className="mt-4">
              <Button variant="ghost" onClick={() => setShowBlockRoomDialog(false)} className="text-vault-text-secondary">
                Cancel
              </Button>
              <Button 
                onClick={handleBlockRoom}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="non-railroad-checkin-btn"
              >
                {(blockRoomForm.check_in_date > getCentralToday() || blockRoomForm.is_reservation) ? 'Create Reservation' : 'Check In Guest'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Block Guest Dialog */}
      <Dialog open={blockGuestDialog} onOpenChange={setBlockGuestDialog}>
        <DialogContent className="bg-vault-dark border-red-600/50 text-vault-text max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-red-400 flex items-center gap-2">
              <Ban className="w-5 h-5" />
              Block Guest
            </DialogTitle>
            <DialogDescription className="text-vault-text-secondary">
              Add this guest to the blacklist. They will be politely declined for future bookings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-red-400 uppercase tracking-wider mb-1 block">Guest Name *</label>
              <Input
                value={blockGuestForm.guest_name}
                onChange={(e) => setBlockGuestForm({...blockGuestForm, guest_name: e.target.value})}
                placeholder="Full name"
                className="bg-black/50 border-red-600/50 text-vault-text"
              />
            </div>
            <div>
              <label className="text-xs text-red-400 uppercase tracking-wider mb-1 block">Email (optional)</label>
              <Input
                value={blockGuestForm.email}
                onChange={(e) => setBlockGuestForm({...blockGuestForm, email: e.target.value})}
                placeholder="email@example.com"
                className="bg-black/50 border-red-600/50 text-vault-text"
              />
            </div>
            <div>
              <label className="text-xs text-red-400 uppercase tracking-wider mb-1 block">Phone (optional)</label>
              <Input
                value={blockGuestForm.phone}
                onChange={(e) => setBlockGuestForm({...blockGuestForm, phone: e.target.value})}
                placeholder="Phone number"
                className="bg-black/50 border-red-600/50 text-vault-text"
              />
            </div>
            <div>
              <label className="text-xs text-red-400 uppercase tracking-wider mb-1 block">Reason for Blocking *</label>
              <Input
                value={blockGuestForm.reason}
                onChange={(e) => setBlockGuestForm({...blockGuestForm, reason: e.target.value})}
                placeholder="e.g., Property damage, Non-payment, Disruptive behavior"
                className="bg-black/50 border-red-600/50 text-vault-text"
              />
            </div>
            <div className="bg-red-900/20 border border-red-600/30 rounded-lg p-3">
              <p className="text-red-300 text-sm">
                ⚠️ Blocked guests will receive a polite decline message when trying to book online:
                <br />
                <em className="text-red-200/70">"We apologize, but we are unable to accommodate your reservation at this time."</em>
              </p>
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setBlockGuestDialog(false)} className="text-vault-text-secondary">
              Cancel
            </Button>
            <Button 
              onClick={handleBlockGuest}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={!blockGuestForm.guest_name || !blockGuestForm.reason}
            >
              <Ban className="w-4 h-4 mr-2" />
              Block Guest
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Multi-Room Booking Dialog */}
      <Dialog open={showMultiRoomBooking} onOpenChange={setShowMultiRoomBooking}>
        <DialogContent className="bg-vault-dark border-vault-border text-vault-text max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit text-xl text-vault-gold flex items-center gap-2">
              <Home className="w-5 h-5" />
              Multi-Room Reservation
            </DialogTitle>
            <DialogDescription className="text-vault-text-secondary">
              Book multiple rooms under one guest name with a single confirmation number.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Guest Information */}
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Guest Name *</label>
                <Input
                  value={multiRoomForm.guest_name}
                  onChange={(e) => setMultiRoomForm({...multiRoomForm, guest_name: e.target.value})}
                  placeholder="Full name"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Email</label>
                <Input
                  type="email"
                  value={multiRoomForm.email}
                  onChange={(e) => setMultiRoomForm({...multiRoomForm, email: e.target.value})}
                  placeholder="email@example.com"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Phone</label>
                <Input
                  value={multiRoomForm.phone}
                  onChange={(e) => setMultiRoomForm({...multiRoomForm, phone: e.target.value})}
                  placeholder="555-123-4567"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Address</label>
                <Input
                  value={multiRoomForm.address}
                  onChange={(e) => setMultiRoomForm({...multiRoomForm, address: e.target.value})}
                  placeholder="123 Main St, City, State ZIP"
                  className="bg-black/50 border-vault-border text-vault-text"
                />
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-In Date *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-black/50 border-vault-border text-vault-text hover:bg-black/70"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {multiRoomForm.check_in_date ? format(new Date(multiRoomForm.check_in_date + 'T00:00:00'), 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-vault-surface border-vault-border" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={multiRoomForm.check_in_date ? new Date(multiRoomForm.check_in_date + 'T00:00:00') : undefined}
                      onSelect={(date) => setMultiRoomForm({...multiRoomForm, check_in_date: date ? format(date, 'yyyy-MM-dd') : ''})}
                      initialFocus
                      className="text-vault-text"
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Check-Out Date *</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal bg-black/50 border-vault-border text-vault-text hover:bg-black/70"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {multiRoomForm.check_out_date ? format(new Date(multiRoomForm.check_out_date + 'T00:00:00'), 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-vault-surface border-vault-border" align="start">
                    <CalendarComponent
                      mode="single"
                      selected={multiRoomForm.check_out_date ? new Date(multiRoomForm.check_out_date + 'T00:00:00') : undefined}
                      onSelect={(date) => setMultiRoomForm({...multiRoomForm, check_out_date: date ? format(date, 'yyyy-MM-dd') : ''})}
                      disabled={(date) => multiRoomForm.check_in_date && date <= new Date(multiRoomForm.check_in_date + 'T00:00:00')}
                      initialFocus
                      className="text-vault-text"
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Room Selection */}
            <div>
              <label className="text-xs text-vault-gold uppercase tracking-wider mb-2 block">Rooms *</label>
              <div className="flex gap-2 mb-3">
                <Button
                  type="button"
                  onClick={() => addRoomToBooking('single')}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Single Bed (${portalSettings.single_room_rate})
                </Button>
                <Button
                  type="button"
                  onClick={() => addRoomToBooking('double')}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  size="sm"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add Double Bed (${portalSettings.double_room_rate})
                </Button>
              </div>
              
              {selectedRooms.length > 0 && (
                <div className="space-y-2 bg-black/30 rounded-lg p-3">
                  {selectedRooms.map((room, idx) => (
                    <div key={room.id} className="flex items-center gap-3 bg-vault-surface p-2 rounded border border-vault-border">
                      <span className="text-vault-gold font-bold w-8">#{idx + 1}</span>
                      <span className={`px-2 py-0.5 rounded text-xs ${room.room_type === 'double' ? 'bg-purple-600/30 text-purple-300' : 'bg-blue-600/30 text-blue-300'}`}>
                        {room.room_type === 'double' ? 'Double Bed' : 'Single Bed'}
                      </span>
                      <Input
                        placeholder="Room # (optional)"
                        value={room.room_number}
                        onChange={(e) => updateRoomInBooking(room.id, 'room_number', e.target.value)}
                        className="w-28 h-8 bg-black/50 border-vault-border text-vault-text text-sm"
                      />
                      <div className="flex items-center gap-1">
                        <span className="text-vault-text-secondary text-xs">$</span>
                        <Input
                          type="number"
                          step="0.01"
                          value={room.room_rate}
                          onChange={(e) => updateRoomInBooking(room.id, 'room_rate', parseFloat(e.target.value) || 0)}
                          className="w-20 h-8 bg-black/50 border-vault-border text-green-400 text-sm font-mono"
                        />
                        <span className="text-vault-text-secondary text-xs">/night</span>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeRoomFromBooking(room.id)}
                        className="text-red-400 hover:text-red-300 h-7 px-2"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs text-vault-gold uppercase tracking-wider mb-1 block">Notes</label>
              <Input
                value={multiRoomForm.notes}
                onChange={(e) => setMultiRoomForm({...multiRoomForm, notes: e.target.value})}
                placeholder="Special requests, group info, etc."
                className="bg-black/50 border-vault-border text-vault-text"
              />
            </div>

            {/* Email Confirmation Toggle */}
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="sendEmail"
                checked={multiRoomForm.send_confirmation_email}
                onChange={(e) => setMultiRoomForm({...multiRoomForm, send_confirmation_email: e.target.checked})}
                className="w-4 h-4"
              />
              <label htmlFor="sendEmail" className="text-vault-text-secondary text-sm">
                Send confirmation email to guest
              </label>
            </div>

            {/* Total Summary with Tax Breakdown */}
            {selectedRooms.length > 0 && multiRoomForm.check_in_date && multiRoomForm.check_out_date && (() => {
              const nights = Math.max(1, Math.ceil((new Date(multiRoomForm.check_out_date) - new Date(multiRoomForm.check_in_date)) / (1000 * 60 * 60 * 24)));
              const ratePerNight = selectedRooms.reduce((sum, r) => sum + r.room_rate, 0);
              const subtotal = ratePerNight * nights;
              const taxPct = portalSettings.sales_tax_rate || 10.3;
              const totalTax = Math.round(subtotal * (taxPct / 100) * 100) / 100;
              const grandTotal = subtotal + totalTax;
              return (
                <div className="bg-vault-gold/10 border border-vault-gold/30 rounded-lg p-4 space-y-1" data-testid="multi-room-cost-breakdown">
                  <div className="flex justify-between items-center">
                    <span className="text-vault-text-secondary">Total Rooms:</span>
                    <span className="text-vault-text font-bold">{selectedRooms.length}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-vault-text-secondary">Nights:</span>
                    <span className="text-vault-text font-bold">{nights}</span>
                  </div>
                  {selectedRooms.map((room, idx) => (
                    <div key={room.id} className="flex justify-between items-center text-sm">
                      <span className="text-vault-text-secondary">Room #{idx + 1} ({room.room_type === 'double' ? 'Double' : 'Single'}):</span>
                      <span className="text-vault-text">${room.room_rate.toFixed(2)} x {nights} = ${(room.room_rate * nights).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between items-center border-t border-vault-gold/30 pt-2 mt-2">
                    <span className="text-vault-text-secondary">Subtotal (Rooms):</span>
                    <span className="text-vault-text font-medium">${subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-vault-text-secondary">Sales Tax ({taxPct}%):</span>
                    <span className="text-vault-text">${totalTax.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between items-center border-t border-vault-gold/30 pt-2 mt-2">
                    <span className="text-vault-gold font-bold">Grand Total:</span>
                    <span className="text-vault-gold font-bold text-xl">${grandTotal.toFixed(2)}</span>
                  </div>
                </div>
              );
            })()}

            {/* Last Confirmation */}
            {lastConfirmation && (
              <div className="bg-green-900/20 border border-green-500/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-400 font-bold">Last Booking Confirmed!</p>
                    <p className="text-green-300 text-2xl font-mono">{lastConfirmation.confirmation_number}</p>
                  </div>
                  <Button
                    onClick={() => copyConfirmationToClipboard(lastConfirmation.confirmation_number)}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    size="sm"
                  >
                    <Copy className="w-4 h-4 mr-1" />
                    Copy
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button variant="ghost" onClick={() => setShowMultiRoomBooking(false)} className="text-vault-text-secondary">
              Cancel
            </Button>
            <Button 
              onClick={handleMultiRoomBooking}
              className="bg-vault-gold hover:bg-vault-gold/80 text-black"
              disabled={multiRoomLoading || !multiRoomForm.guest_name || !multiRoomForm.check_in_date || !multiRoomForm.check_out_date || selectedRooms.length === 0}
            >
              {multiRoomLoading ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Booking...
                </>
              ) : (
                <>
                  <Calendar className="w-4 h-4 mr-2" />
                  Book {selectedRooms.length} Room{selectedRooms.length !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="noise-overlay" />
    </div>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="stat-card rounded-lg"
      data-testid={testId}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-xs uppercase tracking-wider text-vault-gold/80 mb-2">{label}</p>
          <p className="font-outfit text-4xl font-bold text-vault-text">{value}</p>
        </div>
        <div className="text-vault-gold/50">{icon}</div>
      </div>
    </motion.div>
  );
}
